import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Pencil, X, Search, Calendar } from 'lucide-react';
import FotosInstalacaoPanel from '../components/FotosInstalacaoPanel';
import FotosLocalPanel from '../components/FotosLocalPanel';
import useTouchKanban from '../hooks/useTouchKanban';

const STATUS_COLS_LEAD = [
  { key:'aguardando_vendedor', label:'Aguardando Vendedor', color:'#60a5fa' },
  { key:'visita',              label:'Visita Agendada',     color:'#fb923c' },
  { key:'proposta',            label:'Proposta Enviada',    color:'#f6c90e' },
  { key:'em_espera',           label:'Aguardando Cliente',  color:'#f59e0b' },
  { key:'fechado',             label:'Venda Fechada',       color:'#4ade80' },
  { key:'perdido',             label:'Desistiu da Compra',  color:'#f87171' },
];

const STATUS_COLS_POS_VENDA = [
  { key:'aguardando',        label:'Aguardando Pós Venda', color:'#64748b' },
  { key:'na_transportadora', label:'Na Transportadora',    color:'#fb923c' },
  { key:'processo_entrega',  label:'Processo de Entrega',  color:'#facc15' },
  { key:'entregue',          label:'Entregue',             color:'#22d3ee' },
  { key:'instalando',        label:'Instalando',           color:'#a78bfa' },
  { key:'monitoramento',     label:'Monitoramento',        color:'#8b5cf6' },
  { key:'finalizado',        label:'Finalizado',           color:'#4ade80' },
];

const STATUS_COLS_ENG = [
  { key:'aguardando_engenheiro',         label:'Aguardando',               color:'#64748b' },
  { key:'a_pagar_crt',                   label:'A Pagar CRT',              color:'#fb923c' },
  { key:'analise_cemig',                 label:'Análise CEMIG',            color:'#60a5fa' },
  { key:'parecer_aprovado',              label:'Parecer Aprovado',         color:'#4ade80' },
  { key:'parecer_reprovado',             label:'Parecer Reprovado',        color:'#f87171' },
  { key:'aguardando_solicitar_vistoria', label:'Ag. Vistoria',             color:'#a78bfa' },
  { key:'vistoria_solicitada',           label:'Vistoria Solicitada',      color:'#f6c90e' },
  { key:'vistoria_concluida',            label:'Vistoria Concluída',       color:'#4ade80' },
  // legado
  { key:'realizando_planejamento',       label:'Planejamento (leg.)',      color:'#334155' },
  { key:'aguardando_cemig',              label:'Ag. CEMIG (leg.)',         color:'#334155' },
  { key:'documentacao_aprovada',         label:'Doc. Aprovada (leg.)',     color:'#334155' },
  { key:'vistoria_feita',                label:'Vistoria Feita (leg.)',    color:'#334155' },
];

// NOTA: nenhum kanban de status usa mais flowMap com travas — Lead, Pós Venda e
// Engenheiro permitem mover qualquer card para qualquer status, sem restrição de
// fluxo. O único caso especial é o destino "fechado" no kanban de Leads, que
// mantém o fluxo de checagem de documentos (PropostaModal / VendaFechadaModal).

// Dica contextual por status de destino
const MODAL_HINTS = {
  visita:                  'Confirme que a visita técnica foi agendada com o cliente.',
  proposta:                'Confirme que a proposta comercial foi enviada ao cliente.',
  em_espera:               'Confirme que o lead está aguardando retorno do cliente.',
  perdido:                 'Registre que o cliente desistiu da compra.',
  na_transportadora:       'Confirme que o equipamento foi despachado e está em trânsito.',
  instalando:              'Confirme que a equipe iniciou a instalação no imóvel.',
  monitoramento:           'Confirme que o sistema foi ativado e está em monitoramento.',
  finalizado:              'Confirme a conclusão completa do serviço de pós-venda.',
  realizando_planejamento: 'Confirme que o planejamento do projeto foi iniciado.',
  aguardando_cemig:        'Confirme que a documentação foi enviada para a CEMIG.',
  documentacao_aprovada:   'Confirme que a CEMIG aprovou a documentação enviada.',
  a_pagar_crt:              'Confirme que o pagamento do CRT foi realizado.',
  analise_cemig:            'Confirme que a documentação foi enviada para análise da CEMIG.',
  aguardando_solicitar_vistoria: 'Confirme que o parecer foi aprovado e a vistoria pode ser solicitada.',
  vistoria_solicitada:      'Confirme que a vistoria foi solicitada junto à CEMIG.',
  vistoria_concluida:       'Confirme que a vistoria foi concluída com sucesso.',
  vistoria_feita:           'Confirme que a vistoria foi realizada com sucesso.',
};

function validarMovimento(currentStatus, newStatus, flowMap) {
  const permitidos = flowMap[currentStatus] || [];
  return permitidos.includes(newStatus);
}

const S = {
  heading:  { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub:      { fontSize:13, color:'#475569', marginBottom:20 },
  board:    { display:'flex', gap:12, overflowX:'auto', paddingBottom:12, WebkitOverflowScrolling:'touch' },
  col:      (c) => ({ minWidth:190, background:'#0e1420', border:`1px solid ${c}33`, borderRadius:12, padding:10, borderTop:`3px solid ${c}`, flexShrink:0, display:'flex', flexDirection:'column' }),
  cardBody: { overflowY:'auto', WebkitOverflowScrolling:'touch', flex:1, maxHeight:'calc(100vh - 340px)', minHeight:40 },
  colHead:  { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:10 },
  colLabel: (c) => ({ fontSize:11, fontWeight:700, color:c, textTransform:'uppercase', letterSpacing:0.5 }),
  badge:    (c) => ({ background:`${c}22`, color:c, fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:10, border:`1px solid ${c}44` }),
  card:     (drag) => ({ background: drag?'rgba(246,201,14,0.1)':'#111827', borderRadius:8, padding:'10px 11px', marginBottom:7, border:'1px solid #1a2035', cursor:'grab', userSelect:'none' }),
  cardName: { fontSize:13, fontWeight:600, color:'#e2e8f0', marginBottom:3 },
  cardSub:  { fontSize:11, color:'#475569' },
  cardFoot: { display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:8 },
  editBtn:  { background:'rgba(96,165,250,0.15)', border:'1px solid rgba(96,165,250,0.3)', borderRadius:5, padding:'3px 7px', cursor:'pointer', color:'#60a5fa', display:'flex', alignItems:'center' },
  inp: {
    width:'100%', background:'#0e1420', border:'1px solid #1e2d45',
    borderRadius:7, padding:'9px 12px', color:'#e2e8f0', fontSize:13,
    outline:'none', boxSizing:'border-box',
  },
  lbl: { fontSize:11, color:'#94a3b8', fontWeight:600, marginBottom:4, display:'block' },
  preOverlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' },
  preModal:   { background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:420, maxWidth:'95vw' },
};

// ─── Modal genérico de confirmação de mudança de status ─────────────────────
function KanbanModal({ itemName, currentStatus, newStatus, colsMap, flowMap, hint, onConfirm, onCancel, onSelectStatus }) {
  const [visitaDate, setVisitaDate]         = React.useState('');
  const [motivo, setMotivo]                 = React.useState('');
  const [prazo, setPrazo]                   = React.useState('');
  const [dataPrevista, setDataPrevista]     = React.useState('');
  const [dataChegada, setDataChegada]       = React.useState('');
  const [dataConclusao, setDataConclusao]   = React.useState('');
  const [observacoes, setObservacoes]       = React.useState('');
  const [conversaTexto, setConversaTexto]   = React.useState('');
  // flowMap=null/undefined → modo livre: qualquer movimento é considerado válido (sem travas)
  const isValid = !flowMap || validarMovimento(currentStatus, newStatus, flowMap);
  const currentLabel = colsMap[currentStatus] || currentStatus;
  const newLabel     = colsMap[newStatus]     || newStatus;
  const proximosPermitidos = flowMap ? (flowMap[currentStatus] || []) : [];

  // Overlay comum
  const overlay = {
    position:'fixed', inset:0, background:'rgba(0,0,0,0.7)',
    zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center',
  };

  // ── Movimento INVÁLIDO ─────────────────────────────────────────────────────
  if (!isValid) {
    return (
      <div style={overlay}>
        <div style={{ background:'#111827', border:'1px solid rgba(248,113,113,0.35)', borderRadius:14, padding:28, width:460, maxWidth:'95vw' }}>
          <div style={{ fontSize:16, fontWeight:700, color:'#f87171', marginBottom:14 }}>
            ⚠️ Movimento não permitido
          </div>

          <p style={{ color:'#e2e8f0', fontSize:13, margin:'0 0 8px' }}>
            <strong style={{ color:'#f6c90e' }}>{itemName}</strong> está em{' '}
            <span style={{ background:'#1a2035', padding:'2px 8px', borderRadius:5, color:'#94a3b8' }}>{currentLabel}</span>.
          </p>
          <p style={{ color:'#94a3b8', fontSize:13, margin:'0 0 18px', lineHeight:1.5 }}>
            Não é possível mover diretamente para{' '}
            <span style={{ color:'#f87171', fontWeight:600 }}>{newLabel}</span>.<br />
            O(s) próximo(s) status permitido(s) seguindo o fluxo correto:
          </p>

          <div style={{ display:'flex', flexWrap:'wrap', gap:8, marginBottom:22 }}>
            {proximosPermitidos.map(s => (
              <button
                key={s}
                style={{ padding:'8px 18px', borderRadius:8, background:'rgba(96,165,250,0.15)', color:'#60a5fa', border:'1px solid rgba(96,165,250,0.35)', fontSize:13, fontWeight:600, cursor:'pointer' }}
                onClick={() => onSelectStatus(s)}
              >
                {colsMap[s] || s}
              </button>
            ))}
          </div>

          <div style={{ display:'flex', justifyContent:'flex-end' }}>
            <button
              style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}
              onClick={onCancel}
            >
              Cancelar
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Movimento VÁLIDO ───────────────────────────────────────────────────────
  return (
    <div style={overlay}>
      <div style={{ background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:420, maxWidth:'95vw' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:10 }}>
          Confirmar mudança de status
        </div>

        <p style={{ color:'#e2e8f0', fontSize:13, margin:'0 0 6px' }}>
          Mover <strong style={{ color:'#f6c90e' }}>{itemName}</strong>:
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:16, flexWrap:'wrap' }}>
          <span style={{ background:'#1a2035', padding:'4px 12px', borderRadius:6, color:'#94a3b8', fontSize:13 }}>{currentLabel}</span>
          <span style={{ color:'#475569', fontSize:16 }}>→</span>
          <span style={{ background:'rgba(74,222,128,0.12)', border:'1px solid rgba(74,222,128,0.25)', padding:'4px 12px', borderRadius:6, color:'#4ade80', fontSize:13, fontWeight:600 }}>{newLabel}</span>
        </div>

        {hint && (
          <p style={{ color:'#64748b', fontSize:12, margin:'0 0 20px', lineHeight:1.5, padding:'10px 12px', background:'#0e1420', borderRadius:8, borderLeft:'3px solid #1e2d45' }}>
            {hint}
          </p>
        )}

        {/* ── Campos por status de destino ── */}
        {newStatus === 'visita' && (
          <div style={{ marginBottom:14 }}>
            <label style={S.lbl}>Data e Hora da Visita (opcional)</label>
            <input type="datetime-local" value={visitaDate} onChange={e => setVisitaDate(e.target.value)} style={S.inp} />
          </div>
        )}

        {newStatus === 'em_espera' && (
          <>
            <div style={{ marginBottom:10 }}>
              <label style={S.lbl}>O que está aguardando?</label>
              <textarea value={motivo} onChange={e => setMotivo(e.target.value)} style={{ ...S.inp, height:70, resize:'vertical' }} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Data para retornar</label>
              <input type="datetime-local" value={prazo} onChange={e => setPrazo(e.target.value)} style={S.inp} />
            </div>
          </>
        )}

        {newStatus === 'perdido' && (
          <div style={{ marginBottom:14 }}>
            <label style={S.lbl}>Motivo de desistência</label>
            <textarea value={motivo} onChange={e => setMotivo(e.target.value)} style={{ ...S.inp, height:70, resize:'vertical' }} />
          </div>
        )}

        {newStatus === 'na_transportadora' && (
          <>
            <div style={{ marginBottom:10 }}>
              <label style={S.lbl}>Data Prevista de Chegada</label>
              <input type="date" value={dataPrevista} onChange={e => setDataPrevista(e.target.value)} style={S.inp} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Data Real de Chegada (se já chegou)</label>
              <input type="date" value={dataChegada} onChange={e => setDataChegada(e.target.value)} style={S.inp} />
            </div>
          </>
        )}

        {newStatus === 'instalando' && (
          <div style={{ marginBottom:14 }}>
            <label style={S.lbl}>Data e Hora de Início da Instalação</label>
            <input type="datetime-local" value={visitaDate} onChange={e => setVisitaDate(e.target.value)} style={S.inp} />
          </div>
        )}

        {newStatus === 'finalizado' && (
          <>
            <div style={{ marginBottom:10 }}>
              <label style={S.lbl}>Data de Conclusão</label>
              <input type="date" value={dataConclusao} onChange={e => setDataConclusao(e.target.value)} style={S.inp} />
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={S.lbl}>Observações finais</label>
              <textarea value={observacoes} onChange={e => setObservacoes(e.target.value)} style={{ ...S.inp, height:60, resize:'vertical' }} />
            </div>
          </>
        )}

        {/* ── Como foi a conversa ── */}
        <div style={{ marginBottom:16 }}>
          <label style={{ ...S.lbl, color:'#94a3b8' }}>Como foi a conversa? <span style={{ color:'#475569', fontWeight:400 }}>(opcional)</span></label>
          <textarea
            value={conversaTexto}
            onChange={e => setConversaTexto(e.target.value)}
            placeholder="Descreva como foi o contato com o cliente..."
            style={{ ...S.inp, height:80, resize:'vertical' }}
          />
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button
            style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            style={{ padding:'8px 22px', borderRadius:8, background:'rgba(74,222,128,0.15)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.3)', fontSize:13, fontWeight:700, cursor:'pointer' }}
            onClick={() => onConfirm({ visitaDate, motivo, prazo, dataPrevista, dataChegada, dataConclusao, observacoes, conversaTexto })}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal simples de confirmação (sem travas, sem campos extras) ───────────
// Usado pelo Engenheiro: qualquer status → qualquer status, só confirma e muda.
function KitSolarBox({ item }) {
  if (!item || (!item.kit_equipamento && !item.kit_marca)) return null;
  return (
    <div style={{ background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:18, fontSize:12 }}>
      <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📦 Kit Solar</div>
      {[
        ['Equipamento', item.kit_equipamento],
        ['Marca',       item.kit_marca],
        ['Geração',     item.kit_geracao != null ? `${item.kit_geracao} kWh/mês` : null],
        ['Placas',      item.kit_quantidade != null ? `${item.kit_quantidade} un · ${item.kit_placas_w || '—'} W cada` : null],
        ['Valor do Kit', item.kit_valor != null ? `R$ ${Number(item.kit_valor).toLocaleString('pt-BR', { minimumFractionDigits:2 })}` : null],
      ].filter(([, v]) => v).map(([k, v]) => (
        <div key={k} style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
          <span style={{ color:'#64748b' }}>{k}</span>
          <span style={{ color:'#e2e8f0', fontWeight:600 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function SimpleConfirmModal({ itemName, currentStatus, newStatus, colsMap, onConfirm, onCancel, kitData = null }) {
  const currentLabel = colsMap[currentStatus] || currentStatus;
  const newLabel      = colsMap[newStatus]     || newStatus;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:420, maxWidth:'95vw' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:10 }}>
          Confirmar mudança de status
        </div>

        <p style={{ color:'#e2e8f0', fontSize:13, margin:'0 0 6px' }}>
          Mover <strong style={{ color:'#f6c90e' }}>{itemName}</strong>:
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18, flexWrap:'wrap' }}>
          <span style={{ background:'#1a2035', padding:'4px 12px', borderRadius:6, color:'#94a3b8', fontSize:13 }}>{currentLabel}</span>
          <span style={{ color:'#475569', fontSize:16 }}>→</span>
          <span style={{ background:'rgba(74,222,128,0.12)', border:'1px solid rgba(74,222,128,0.25)', padding:'4px 12px', borderRadius:6, color:'#4ade80', fontSize:13, fontWeight:600 }}>{newLabel}</span>
        </div>

        <KitSolarBox item={kitData} />

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button
            style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            style={{ padding:'8px 22px', borderRadius:8, background:'rgba(74,222,128,0.15)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.3)', fontSize:13, fontWeight:700, cursor:'pointer' }}
            onClick={onConfirm}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal de Data da Entrega (obrigatório ao arrastar para "Entregue") ─────
// Os 3 campos são obrigatórios: data de entrega, instalador designado e
// data prevista da instalação (data_prevista — programada pelo pós-venda,
// depois confirmada ou alterada pelo instalador).
function EntregaDataModal({ itemName, currentStatus, newStatus, colsMap, onConfirm, onCancel, instaladoresList = [], defaultInstaladorId = '', defaultDataPrevista = '', kitData = null }) {
  const [dataEntrega, setDataEntrega]     = useState('');
  const [instaladorId, setInstaladorId]   = useState(defaultInstaladorId || '');
  const [dataPrevista, setDataPrevista]   = useState(defaultDataPrevista || '');
  const currentLabel = colsMap[currentStatus] || currentStatus;
  const newLabel      = colsMap[newStatus]     || newStatus;
  const fieldStyle = { width:'100%', padding:'9px 12px', borderRadius:8, background:'#0f1117', border:'1px solid #1e2d45', color:'#e2e8f0', fontSize:13 };
  const podeConfirmar = Boolean(dataEntrega && instaladorId && dataPrevista);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:440, maxWidth:'95vw' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:10 }}>
          Confirmar mudança de status
        </div>

        <p style={{ color:'#e2e8f0', fontSize:13, margin:'0 0 6px' }}>
          Mover <strong style={{ color:'#f6c90e' }}>{itemName}</strong>:
        </p>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:18, flexWrap:'wrap' }}>
          <span style={{ background:'#1a2035', padding:'4px 12px', borderRadius:6, color:'#94a3b8', fontSize:13 }}>{currentLabel}</span>
          <span style={{ color:'#475569', fontSize:16 }}>→</span>
          <span style={{ background:'rgba(74,222,128,0.12)', border:'1px solid rgba(74,222,128,0.25)', padding:'4px 12px', borderRadius:6, color:'#4ade80', fontSize:13, fontWeight:600 }}>{newLabel}</span>
        </div>

        <KitSolarBox item={kitData} />

        <div style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:12, color:'#94a3b8', marginBottom:6 }}>Data da Entrega *</label>
          <input type="date" value={dataEntrega} onChange={e => setDataEntrega(e.target.value)} style={fieldStyle} />
        </div>

        <div style={{ marginBottom:14 }}>
          <label style={{ display:'block', fontSize:12, color:'#94a3b8', marginBottom:6 }}>Instalador Designado *</label>
          <select value={instaladorId} onChange={e => setInstaladorId(e.target.value)} style={fieldStyle}>
            <option value="">— Selecionar instalador —</option>
            {instaladoresList.map(i => <option key={i.id} value={i.id}>{i.nome}</option>)}
          </select>
        </div>
        <div style={{ marginBottom:22 }}>
          <label style={{ display:'block', fontSize:12, color:'#94a3b8', marginBottom:6 }}>Data Prevista da Instalação *</label>
          <input type="date" value={dataPrevista} onChange={e => setDataPrevista(e.target.value)} style={fieldStyle} />
        </div>

        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button
            style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}
            onClick={onCancel}
          >
            Cancelar
          </button>
          <button
            disabled={!podeConfirmar}
            style={{ padding:'8px 22px', borderRadius:8, background: podeConfirmar ? 'rgba(74,222,128,0.15)' : 'rgba(74,222,128,0.06)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.3)', fontSize:13, fontWeight:700, cursor: podeConfirmar ? 'pointer' : 'default', opacity: podeConfirmar ? 1 : 0.5 }}
            onClick={() => podeConfirmar && onConfirm({ dataEntrega, instaladorId, dataPrevista })}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Indicador de documento (ok/pendente) ────────────────────────────────────
function DocCheck({ label, ok }) {
  return (
    <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:12 }}>
      <span style={{ width:16, height:16, borderRadius:'50%', background: ok ? 'rgba(74,222,128,0.2)' : 'rgba(248,113,113,0.15)', border: `1px solid ${ok ? '#4ade80' : '#f87171'}`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <span style={{ color: ok ? '#4ade80' : '#f87171', fontSize:10, fontWeight:900 }}>{ok ? '✓' : '✗'}</span>
      </span>
      <span style={{ color: ok ? '#94a3b8' : '#f87171' }}>{label}</span>
    </div>
  );
}

// ─── Modal de confirmação de Venda Fechada ───────────────────────────────────
function VendaFechadaModal({ lead, onConfirm, onCancel, erro }) {
  const [nome, setNome] = useState(lead.cliente_nome || '');
  const [rg, setRg]     = useState(lead.cliente_rg_doc || lead.cliente_rg || '');
  const [cpf, setCpf]   = useState(lead.cliente_cpf_doc || lead.cliente_cpf_cnpj || '');
  const [conversaTexto, setConversaTexto] = useState('');

  const candidatos = lead.kits_propostos || [];
  const [kitId, setKitId] = useState(
    lead.kit ? String(lead.kit) : (candidatos.length === 1 ? String(candidatos[0].kit_id) : '')
  );
  const [kits, setKits] = useState([]);
  const [kitSearch, setKitSearch] = useState('');

  useEffect(() => {
    if (candidatos.length === 0) {
      api.get('/kits/').then(r => setKits(r.data));
    }
  }, []);

  const fmtBRL = (v) => Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2 });

  const kitsFiltrados = kitSearch.trim()
    ? kits.filter(k =>
        k.equipamento.toLowerCase().includes(kitSearch.toLowerCase()) ||
        String(k.geracao).includes(kitSearch)
      )
    : kits;

  const secDocs = [
    { label: 'Proposta gerada',        ok: lead.proposta_gerada },
    { label: 'Procuração gerada', ok: lead.cemig_gerado },
    { label: 'Contrato gerado',        ok: lead.contrato_gerado },
  ];

  const CheckSection = ({ title, items }) => {
    const sectionOk = items.every(d => d.ok);
    return (
      <div style={{ background:'#0e1420', border:`1px solid ${sectionOk ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)'}`, borderRadius:8, padding:'10px 12px', marginBottom:10 }}>
        <div style={{ fontSize:10, color:'#64748b', textTransform:'uppercase', letterSpacing:1, marginBottom:7 }}>{title}</div>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'5px 10px' }}>
          {items.map(d => <DocCheck key={d.label} label={d.label} ok={d.ok} />)}
        </div>
      </div>
    );
  };

  const handleConfirmar = () => {
    if (!kitId) { alert('Selecione o kit solar antes de fechar a venda.'); return; }
    let valorTotal = null;
    const candidatoEscolhido = candidatos.find(c => String(c.kit_id) === String(kitId));
    if (candidatoEscolhido && candidatoEscolhido.valor_proposto != null) {
      valorTotal = candidatoEscolhido.valor_proposto;
    } else {
      const kitObj = kits.find(k => String(k.id) === String(kitId));
      if (kitObj?.valor) valorTotal = kitObj.valor;
    }
    onConfirm({ nome, rg, cpf_cnpj: cpf, kit_id: kitId, valor_total_venda: valorTotal, conversaTexto });
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#111827', border:'1px solid rgba(74,222,128,0.25)', borderRadius:14, padding:28, width:480, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#4ade80', marginBottom:4 }}>Confirmar Venda Fechada</div>
        <div style={{ fontSize:12, color:'#475569', marginBottom:16 }}>
          Confirme e complete os dados do cliente antes de fechar a venda.
        </div>

        <CheckSection title="Documentos gerados" items={secDocs} />

        {/* ── Confirme o Kit ── */}
        <div style={{ background:'#0e1420', border:'1px solid rgba(246,201,14,0.2)', borderRadius:8, padding:'10px 12px', marginBottom:14 }}>
          <div style={{ fontSize:10, color:'#f6c90e', textTransform:'uppercase', letterSpacing:1, marginBottom:8, fontWeight:700 }}>📦 Confirme o Kit</div>

          {candidatos.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
              {candidatos.map((c, i) => {
                const sel = String(c.kit_id) === String(kitId);
                return (
                  <div
                    key={c.kit_id || i}
                    onClick={() => setKitId(String(c.kit_id))}
                    style={{ cursor:'pointer', background: sel ? 'rgba(246,201,14,0.08)' : '#080c14', border: sel ? '1px solid #f6c90e' : '1px solid #1a2035', borderRadius:8, padding:'9px 11px', display:'flex', alignItems:'center', gap:9 }}
                  >
                    <div style={{ width:14, height:14, borderRadius:'50%', border:`2px solid ${sel ? '#f6c90e' : '#2d3748'}`, background: sel ? '#f6c90e' : 'transparent', flexShrink:0 }}/>
                    <div>
                      <div style={{ fontSize:12, fontWeight:700, color:'#e2e8f0' }}>
                        {candidatos.length > 1 ? `Opção ${c.ordem || i + 1} · ` : ''}{c.kit_marca ? `${c.kit_marca} ` : ''}{c.kit_equipamento || '—'}
                      </div>
                      <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>
                        {c.kit_geracao ? `${c.kit_geracao} kWh/mês · ` : ''}
                        {c.kit_qtd_inversores != null ? `${c.kit_qtd_inversores} inversor(es) · ` : ''}
                        {c.valor_proposto != null ? <span style={{ color:'#4ade80', fontWeight:600 }}>R$ {fmtBRL(c.valor_proposto)}</span> : '—'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              <div style={{ fontSize:11, color:'#f87171', marginBottom:8 }}>
                Nenhum kit foi proposto para esta venda. Selecione o kit na lista abaixo:
              </div>
              <div style={{ display:'flex', alignItems:'center', gap:6, background:'#080c14', border:'1px solid #1a2035', borderRadius:7, padding:'6px 10px', marginBottom:8 }}>
                <Search size={13} color="#475569"/>
                <input
                  style={{ background:'none', border:'none', color:'#e2e8f0', fontSize:12, outline:'none', flex:1 }}
                  placeholder="Buscar equipamento ou geração..."
                  value={kitSearch}
                  onChange={e => setKitSearch(e.target.value)}
                />
              </div>
              <div style={{ maxHeight:160, overflowY:'auto', display:'flex', flexDirection:'column', gap:6 }}>
                {kits.length === 0 ? (
                  <div style={{ fontSize:12, color:'#475569', textAlign:'center', padding:10 }}>Carregando kits...</div>
                ) : kitsFiltrados.length === 0 ? (
                  <div style={{ fontSize:12, color:'#475569', textAlign:'center', padding:10 }}>Nenhum kit encontrado</div>
                ) : kitsFiltrados.map(k => {
                  const sel = String(k.id) === String(kitId);
                  return (
                    <div
                      key={k.id}
                      onClick={() => setKitId(String(k.id))}
                      style={{ cursor:'pointer', background: sel ? 'rgba(246,201,14,0.08)' : '#080c14', border: sel ? '1px solid #f6c90e' : '1px solid #1a2035', borderRadius:8, padding:'9px 11px', display:'flex', alignItems:'center', gap:9 }}
                    >
                      <div style={{ width:14, height:14, borderRadius:'50%', border:`2px solid ${sel ? '#f6c90e' : '#2d3748'}`, background: sel ? '#f6c90e' : 'transparent', flexShrink:0 }}/>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12, fontWeight:700, color:'#e2e8f0' }}>{k.marca ? `${k.marca} ` : ''}{k.equipamento}</div>
                        <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>{k.geracao} kWh/mês · {k.qtd_inversores != null ? `${k.qtd_inversores} inversor(es) · ` : ''}<span style={{ color:'#4ade80', fontWeight:600 }}>R$ {fmtBRL(k.valor)}</span></div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            <label style={S.lbl}>Nome Completo</label>
            <input style={S.inp} value={nome} onChange={e => setNome(e.target.value)} placeholder="Nome completo do cliente" />
          </div>
          <div>
            <label style={S.lbl}>RG</label>
            <input style={S.inp} value={rg} onChange={e => setRg(e.target.value)} placeholder="RG do cliente" />
          </div>
          <div>
            <label style={S.lbl}>CPF / CNPJ</label>
            <input style={S.inp} value={cpf} onChange={e => setCpf(e.target.value)} placeholder="CPF ou CNPJ do cliente" />
          </div>
        </div>

        {erro && (
          <div style={{ marginTop:14, background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:12 }}>
            ⚠️ {erro}
          </div>
        )}

        {/* Como foi a conversa */}
        <div style={{ marginTop:14 }}>
          <label style={{ ...S.lbl, color:'#94a3b8' }}>Como foi a conversa? <span style={{ color:'#475569', fontWeight:400 }}>(opcional)</span></label>
          <textarea
            value={conversaTexto}
            onChange={e => setConversaTexto(e.target.value)}
            placeholder="Descreva como foi o contato com o cliente..."
            style={{ ...S.inp, height:70, resize:'vertical' }}
          />
        </div>

        {/* Fotos do Local — opcional */}
        <FotosLocalPanel leadId={lead.id} readonly={false} />

        <div style={{ display:'flex', gap:10, marginTop:16, justifyContent:'flex-end' }}>
          <button
            style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}
            onClick={onCancel}>
            Cancelar
          </button>
          <button
            disabled={!kitId}
            style={{ padding:'8px 20px', borderRadius:8, background: kitId ? 'rgba(74,222,128,0.15)' : '#1a2035', color: kitId ? '#4ade80' : '#475569', border: kitId ? '1px solid rgba(74,222,128,0.3)' : '1px solid #2d3748', fontSize:13, fontWeight:700, cursor: kitId ? 'pointer' : 'not-allowed' }}
            onClick={handleConfirmar}>
            Confirmar Venda
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Modal: Apresentou a proposta? ──────────────────────────────────────────
function PropostaModal({ lead, onConfirm, onCancel }) {
  const [resposta, setResposta] = React.useState('sim');
  const fmtDT = (d) => d ? new Date(d).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : null;
  const dataVisita = fmtDT(lead.data_visita);
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:400, maxWidth:'95vw' }}>
        <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:16 }}>Fechar Venda</div>
        <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
          Cliente: <strong style={{ color:'#e2e8f0' }}>{lead.cliente_nome}</strong>
        </div>
        {dataVisita && (
          <div style={{ background:'rgba(251,146,60,0.08)', border:'1px solid rgba(251,146,60,0.25)', borderRadius:8, padding:'10px 14px', marginBottom:16 }}>
            <div style={{ fontSize:10, color:'#fb923c', textTransform:'uppercase', letterSpacing:1, fontWeight:700, marginBottom:4 }}>Visita agendada para</div>
            <div style={{ fontSize:14, color:'#e2e8f0', fontWeight:600 }}>{dataVisita}</div>
          </div>
        )}
        <div style={{ marginBottom:20 }}>
          <label style={S.lbl}>Apresentou a proposta?</label>
          <select value={resposta} onChange={e => setResposta(e.target.value)} style={S.inp}>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </div>
        <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
          <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={onCancel}>Cancelar</button>
          <button
            style={{ padding:'8px 20px', borderRadius:8, background: resposta === 'sim' ? 'rgba(74,222,128,0.15)' : 'rgba(248,113,113,0.15)', color: resposta === 'sim' ? '#4ade80' : '#f87171', border:`1px solid ${resposta === 'sim' ? 'rgba(74,222,128,0.3)' : 'rgba(248,113,113,0.3)'}`, fontSize:13, fontWeight:700, cursor:'pointer' }}
            onClick={() => onConfirm(resposta === 'sim')}
          >Confirmar</button>
        </div>
      </div>
    </div>
  );
}


// ─── Kanban reutilizável para Pós-Venda e Engenheiro ─────────────────────────
// freeMode=true (usado por Pós Venda e Engenheiro): sem travas (flowMap/
// noDropKeys ignorados), card sempre arrastável p/ qualquer status, modal de
// confirmação simples (sem campos extras), PATCH só com {status}.
function KanbanBoard({ title, sub, cols, endpoint, isFinalKey, noDropKeys, flowMap, freeMode, instaladoresList = [] }) {
  const navigate = useNavigate();
  const [items, setItems]       = useState([]);
  const [dragging, setDragging] = useState(null);
  const [moveModal, setMoveModal] = useState(null); // { item, newStatus }
  const dragOver = useRef(null);
  const draggingRef = useRef(null); // backup para evitar stale closure no onDrop
  // Touch drag — iOS não suporta HTML5 DnD; usamos touch events
  const handleDropLogicBoard = (current, newStatus) => {
    if (!freeMode && noDropKeys?.includes(newStatus)) return;
    if (!current || current.status === newStatus) return;
    if (freeMode && newStatus === 'processo_entrega') { saveStatusDireto(current, newStatus); return; }
    setMoveModal({ item: current, newStatus });
  };
  const {
    boardRef: boardRefBoard,
    touchDragId: touchDragIdBoard,
    touchOverCol: touchOverColBoard,
    onCardTouchStart: onCardTouchStartBoard,
    onBoardTouchEnd: onBoardTouchEndBoard,
    clearTouchOnDragStart: clearTouchBoard,
  } = useTouchKanban(handleDropLogicBoard);

  const colsMap = Object.fromEntries(cols.map(c => [c.key, c.label]));
  const [loadErro, setLoadErro] = useState('');

  const load = () => {
    setLoadErro('');
    api.get(`/${endpoint}/`).then(r => setItems(r.data)).catch(() => setLoadErro('Erro ao carregar dados. Tente atualizar a página.'));
  };
  useEffect(() => { load(); }, []);

  const byStatus = (key) => items.filter(i => i.status === key);

  const onDragStart = (e, item) => {
    clearTouchBoard(); // Android Chrome: desativa touch para o HTML5 DnD assumir
    draggingRef.current = item;
    setDragging(item);
    e.dataTransfer.effectAllowed = 'move';
  };

  // Salva direto, sem modal — usado para status que não pedem confirmação/dados extras.
  const saveStatusDireto = async (item, newStatus) => {
    try {
      await api.patch(`/${endpoint}/${item.id}/`, { status: newStatus });
      load();
    } catch (err) {
      console.error('Erro ao atualizar status:', err);
    }
  };

  const onDrop = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    handleDropLogicBoard(current, newStatus);
  };

  const confirm = async (extra = {}) => {
    if (!moveModal) return;
    const movedId = moveModal.item.id;
    const ns = moveModal.newStatus;
    const body = { status: ns };
    if (!freeMode) {
      const { visitaDate, dataChegada, dataConclusao, observacoes } = extra;
      if (ns === 'instalando' && visitaDate) body.data_inicio = visitaDate;
      if (ns === 'finalizado') {
        if (dataConclusao) body.data_conclusao = dataConclusao;
        if (observacoes)   body.observacoes = observacoes;
      }
      if (dataChegada) body.data_conclusao = dataChegada;
    }
    if (ns === 'entregue' && extra.dataEntrega) body.data_entrega = extra.dataEntrega;
    await api.patch(`/${endpoint}/${movedId}/`, body);
    // Designar instalador + data prevista no registro Instalador (o backend avança
    // o status do instalador para "instalacao_programada" automaticamente)
    if (ns === 'entregue' && extra.instaladorId && extra.dataPrevista) {
      const instJobId = moveModal.item.instalador_job_id;
      if (instJobId) {
        await api.patch(`/instalador/${instJobId}/`, { instalador: extra.instaladorId, data_prevista: extra.dataPrevista }).catch(() => {});
      }
    }
    setMoveModal(null);
    load();
  };

  return (
    <div>
      <div style={S.heading}>{title}</div>
      <div style={S.sub}>{sub}</div>

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      {moveModal && (
        freeMode && moveModal.newStatus === 'entregue' ? (
          <EntregaDataModal
            itemName={moveModal.item.cliente_nome}
            currentStatus={moveModal.item.status}
            newStatus={moveModal.newStatus}
            colsMap={colsMap}
            onConfirm={(extra) => confirm(extra)}
            onCancel={() => setMoveModal(null)}
            instaladoresList={instaladoresList}
            defaultDataPrevista={moveModal.item.data_prevista_instalacao || ''}
            kitData={moveModal.item}
          />
        ) : freeMode ? (
          <SimpleConfirmModal
            itemName={moveModal.item.cliente_nome}
            currentStatus={moveModal.item.status}
            newStatus={moveModal.newStatus}
            colsMap={colsMap}
            onConfirm={() => confirm()}
            onCancel={() => setMoveModal(null)}
            kitData={moveModal.item}
          />
        ) : (
          <KanbanModal
            itemName={moveModal.item.cliente_nome}
            currentStatus={moveModal.item.status}
            newStatus={moveModal.newStatus}
            colsMap={colsMap}
            flowMap={flowMap}
            hint={MODAL_HINTS[moveModal.newStatus]}
            onConfirm={confirm}
            onCancel={() => setMoveModal(null)}
            onSelectStatus={(s) => setMoveModal(m => ({ ...m, newStatus: s }))}
          />
        )
      )}

      <div ref={boardRefBoard} style={S.board} onTouchEnd={onBoardTouchEndBoard}>
        {cols.map(col => {
          const colItems = byStatus(col.key);
          const final = !freeMode && col.key === isFinalKey;
          const isTouchOver = touchOverColBoard === col.key;
          return (
            <div key={col.key}
              data-col-key={col.key}
              style={{
                ...S.col(col.color),
                ...(isTouchOver ? { border: `2px solid ${col.color}`, background: `${col.color}11` } : {}),
              }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; dragOver.current = col.key; }}
              onDrop={e => onDrop(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{colItems.length}</span>
              </div>
              <div style={S.cardBody}>
              {colItems.map(item => (
                <div key={item.id}
                  draggable={!final}
                  onDragStart={e => !final && onDragStart(e, item)}
                  onTouchStart={e => !final && onCardTouchStartBoard(e, item)}
                  style={S.card(dragging?.id === item.id || touchDragIdBoard === item.id)}>
                  <div style={S.cardName}>{item.cliente_nome}</div>
                  <div style={S.cardSub}>{item.cliente_cidade || ''}</div>
                  <div style={S.cardFoot}>
                    {item.valor_total_venda
                      ? <span style={{ fontSize:11, color:'#f6c90e', fontWeight:600 }}>R$ {Number(item.valor_total_venda).toLocaleString('pt-BR')}</span>
                      : <span />}
                    {!final
                      ? <button style={S.editBtn} onClick={() => navigate('/clientes', { state: { leadId: item.lead, from: '/controle' } })}>
                          <Pencil size={11} />
                        </button>
                      : <span style={{ fontSize:10, color:'#4ade80', fontWeight:600 }}>✅ Concluído</span>
                    }
                  </div>
                </div>
              ))}
              {colItems.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0', pointerEvents:'none' }}>Vazio</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Kanban de leads (chefe / vendedor / pré-venda) ──────────────────────────
function KanbanLeads() {
  const navigate = useNavigate();
  const { isVendedor } = useAuth();
  const [leads, setLeads]         = useState([]);
  const [dragging, setDragging]   = useState(null);
  const [moveModal, setMoveModal] = useState(null); // { lead, newStatus }
  const [vendaModal, setVendaModal] = useState(null); // { lead, erro }
  const [propostaModal, setPropostaModal] = useState(null); // { lead }
  const dragOver  = useRef(null);
  const draggingRef = useRef(null); // backup para evitar stale closure

  const colsMap = Object.fromEntries(STATUS_COLS_LEAD.map(c => [c.key, c.label]));
  const [loadErro, setLoadErro] = useState('');

  // ── Filtro de data (criação do lead) ─────────────────────────────────────────
  const [filtroDataIni, setFiltroDataIni] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [inputDataIni, setInputDataIni]   = useState('');
  const [inputDataFim, setInputDataFim]   = useState('');
  // ── Busca de cliente ─────────────────────────────────────────────────────────
  const searchLRef                        = useRef(null);
  const [searchLQuery, setSearchLQuery]   = useState('');
  const [showLSugg, setShowLSugg]         = useState(false);
  const [selectedLLead, setSelectedLLead] = useState(null);

  const load = () => {
    setLoadErro('');
    api.get('/leads/').then(r => setLeads(r.data)).catch(() => setLoadErro('Erro ao carregar dados. Tente atualizar a página.'));
  };
  useEffect(() => { load(); }, []);

  // Fechar dropdown de busca ao clicar/tocar fora (mousedown + touchstart para mobile)
  useEffect(() => {
    const h = (e) => {
      if (searchLRef.current && !searchLRef.current.contains(e.target)) setShowLSugg(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h, { passive: true });
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, []);

  // Leads filtrados por data de criação
  const leadsDataL = (filtroDataIni || filtroDataFim)
    ? leads.filter(l => {
        // Converte criado_em (UTC) para data BRT antes de comparar
        const d = l.criado_em ? _brDateISO(l.criado_em) : '';
        if (filtroDataIni && d < filtroDataIni) return false;
        if (filtroDataFim && d > filtroDataFim) return false;
        return true;
      })
    : leads;

  // Leads filtrados também por cliente selecionado
  const leadsFilteredL = selectedLLead
    ? leadsDataL.filter(l => l.id === selectedLLead.id)
    : leadsDataL;

  // Sugestões de busca
  const suggL = searchLQuery.length >= 3
    ? leadsDataL.filter(l => l.cliente_nome?.toLowerCase().includes(searchLQuery.toLowerCase())).slice(0, 8)
    : [];

  // Atalhos de data — calculados em BRT para consistência com o filtro
  const _lIsoHoje   = _brDateISO(new Date());
  const _lIsoSemAtr = _brDateISO(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const _lIsoMesAtr = (() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return _brDateISO(d); })();
  const ativoSemL = filtroDataIni === _lIsoSemAtr && filtroDataFim === _lIsoHoje;
  const ativoMesL = filtroDataIni === _lIsoMesAtr && filtroDataFim === _lIsoHoje;
  const btnDataStyleL = (ativo) => ({
    background: ativo ? 'rgba(246,201,14,0.12)' : 'transparent',
    color: ativo ? '#f6c90e' : '#64748b',
    border: `1px solid ${ativo ? 'rgba(246,201,14,0.35)' : '#1a2035'}`,
    borderRadius:8, padding:'7px 12px', fontSize:12, fontWeight:600,
    cursor:'pointer', whiteSpace:'nowrap',
  });

  const byStatus = (key) => leadsFilteredL.filter(l => l.status === key);

  // Lógica de drop extraída — usada tanto pelo HTML5 DnD (desktop/Android Chrome)
  // quanto pelo touch drag (iOS Safari e outros mobile).
  const handleDropLogicLeads = (current, newStatus) => {
    if (!current || current.status === newStatus) return;
    if (newStatus === 'fechado') {
      if (current.status === 'proposta' || current.status === 'em_espera') {
        setPropostaModal({ lead: current });
      } else {
        setVendaModal({ lead: current, erro: '' });
      }
      return;
    }
    setMoveModal({ lead: current, newStatus });
  };

  // Hook de touch drag para iOS e outros mobile sem suporte a HTML5 DnD
  const {
    boardRef: boardRefLeads,
    touchDragId: touchDragIdLeads,
    touchOverCol: touchOverColLeads,
    onCardTouchStart: onCardTouchStartLeads,
    onBoardTouchEnd: onBoardTouchEndLeads,
    clearTouchOnDragStart: clearTouchLeads,
  } = useTouchKanban(handleDropLogicLeads);

  const onDragStart = (e, lead) => {
    clearTouchLeads(); // Android Chrome: desativa touch para o HTML5 DnD assumir
    draggingRef.current = lead;
    setDragging(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    handleDropLogicLeads(current, newStatus);
  };

  const salvarConversa = async (leadId, texto) => {
    if (!texto || !texto.trim()) return;
    try { await api.post(`/leads/${leadId}/conversas/`, { texto: texto.trim() }); } catch (e) {
      alert('Erro ao salvar conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    }
  };

  const confirm = async ({ visitaDate, motivo, prazo, dataPrevista, dataChegada, dataConclusao, observacoes, conversaTexto } = {}) => {
    if (!moveModal) return;
    try {
      const ns = moveModal.newStatus;
      const body = { status: ns };
      if (ns === 'visita'    && visitaDate)      body.data_visita = visitaDate;
      if (ns === 'em_espera') {
        if (motivo) body.motivo_espera = motivo;
        if (prazo)  body.prazo_resposta_vendedor = prazo;
      }
      if (ns === 'perdido'   && motivo)          body.motivo_desistencia = motivo;
      await api.patch(`/leads/${moveModal.lead.id}/`, body);
      await salvarConversa(moveModal.lead.id, conversaTexto);
    } catch (err) {
      alert('Erro ao salvar: ' + (err?.response?.data?.error || err?.response?.data?.erro || err?.message || 'tente novamente'));
    }
    setMoveModal(null);
    load();
  };

  // Ao selecionar um status no modal de erro, verifica se é fechado
  const handleSelectStatus = (s) => {
    if (s === 'fechado' && moveModal) {
      setVendaModal({ lead: moveModal.lead, erro: '' });
      setMoveModal(null);
    } else {
      setMoveModal(m => ({ ...m, newStatus: s }));
    }
  };

  const confirmVenda = async ({ nome, rg, cpf_cnpj, kit_id, valor_total_venda, conversaTexto }) => {
    if (!vendaModal) return;
    try {
      const body = {
        status: 'fechado',
        cliente_nome: nome,
        rg,
        cpf_cnpj,
        kit: kit_id,
      };
      if (valor_total_venda != null) body.valor_total_venda = valor_total_venda;
      await api.patch(`/leads/${vendaModal.lead.id}/`, body);
      await salvarConversa(vendaModal.lead.id, conversaTexto);
      setVendaModal(null);
      load();
    } catch (err) {
      const msg = err?.response?.data?.erro || 'Erro ao confirmar venda.';
      setVendaModal(v => ({ ...v, erro: msg }));
    }
  };

  return (
    <div>
      <div style={S.heading}>Controle</div>
      <div style={S.sub}>Kanban de clientes — arraste para mudar o status</div>

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      {vendaModal && (
        <VendaFechadaModal
          lead={vendaModal.lead}
          erro={vendaModal.erro}
          onConfirm={confirmVenda}
          onCancel={() => setVendaModal(null)}
        />
      )}

      {propostaModal && (
        <PropostaModal
          lead={propostaModal.lead}
          onConfirm={(apresentou) => {
            const lead = propostaModal.lead;
            setPropostaModal(null);
            if (apresentou) setVendaModal({ lead, erro: '' });
          }}
          onCancel={() => setPropostaModal(null)}
        />
      )}

      {moveModal && (
        <KanbanModal
          itemName={moveModal.lead.cliente_nome}
          currentStatus={moveModal.lead.status}
          newStatus={moveModal.newStatus}
          colsMap={colsMap}
          flowMap={null}
          hint={MODAL_HINTS[moveModal.newStatus]}
          onConfirm={confirm}
          onCancel={() => setMoveModal(null)}
          onSelectStatus={handleSelectStatus}
        />
      )}

      {/* ── Filtro de data de criação ──────────────────────────────────────────── */}
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:10 }}>
        <button onClick={() => {
          setInputDataIni(_kIsoToDDMM(_lIsoSemAtr)); setInputDataFim(_kIsoToDDMM(_lIsoHoje));
          setFiltroDataIni(_lIsoSemAtr); setFiltroDataFim(_lIsoHoje);
        }} style={btnDataStyleL(ativoSemL)}>Análise semanal</button>
        <button onClick={() => {
          setInputDataIni(_kIsoToDDMM(_lIsoMesAtr)); setInputDataFim(_kIsoToDDMM(_lIsoHoje));
          setFiltroDataIni(_lIsoMesAtr); setFiltroDataFim(_lIsoHoje);
        }} style={btnDataStyleL(ativoMesL)}>Análise mensal</button>
        <div style={{ display:'flex', alignItems:'center', background:'#0f1117', border:'1px solid #1a2035', borderRadius:8, overflow:'hidden' }}>
          <DatePickerInputK value={inputDataIni} onChange={setInputDataIni} />
          <span style={{ color:'#475569', fontSize:12, padding:'0 2px', userSelect:'none' }}>até</span>
          <DatePickerInputK value={inputDataFim} onChange={setInputDataFim} />
        </div>
        <button
          onClick={() => { setFiltroDataIni(_kDdmmToISO(inputDataIni)); setFiltroDataFim(_kDdmmToISO(inputDataFim)); }}
          style={{ padding:'7px 14px', borderRadius:8, background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)', color:'#60a5fa', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
        >Buscar</button>
        {(filtroDataIni || filtroDataFim) && (
          <button onClick={() => { setFiltroDataIni(''); setFiltroDataFim(''); setInputDataIni(''); setInputDataFim(''); }}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:12, padding:0 }}>
            ✕ Limpar datas
          </button>
        )}
      </div>

      {/* ── Busca de cliente ───────────────────────────────────────────────────── */}
      <div ref={searchLRef} style={{ position:'relative', marginBottom:12, maxWidth:340 }}>
        <div style={{ display:'flex', alignItems:'center', background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, padding:'6px 12px', gap:8 }}>
          <Search size={15} color="#64748b" />
          <input
            value={searchLQuery}
            onChange={e => { setSearchLQuery(e.target.value); setShowLSugg(true); if (!e.target.value) setSelectedLLead(null); }}
            onFocus={() => { if (searchLQuery.length >= 3) setShowLSugg(true); }}
            placeholder="Buscar cliente por nome (mín. 3 letras)…"
            style={{ flex:1, background:'transparent', border:'none', outline:'none', color:'#e2e8f0', fontSize:13 }}
          />
          {(selectedLLead || searchLQuery) && (
            <button onClick={() => { setSearchLQuery(''); setSelectedLLead(null); setShowLSugg(false); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', padding:0, lineHeight:1, display:'flex' }}>
              <X size={15} />
            </button>
          )}
        </div>
        {showLSugg && suggL.length > 0 && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, zIndex:200, marginTop:4, maxHeight:220, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
            {suggL.map(l => (
              <div key={l.id}
                onClick={() => { setSelectedLLead(l); setSearchLQuery(l.cliente_nome || ''); setShowLSugg(false); }}
                style={{ padding:'9px 14px', cursor:'pointer', color:'#e2e8f0', fontSize:13, borderBottom:'1px solid #0f172a' }}
                onMouseEnter={e => e.currentTarget.style.background = '#253048'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {l.cliente_nome}
                {l.cliente_cidade ? <span style={{ color:'#64748b', fontSize:12, marginLeft:6 }}>{l.cliente_cidade}</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ref no board + onTouchEnd para suporte iOS (touch drag) */}
      <div ref={boardRefLeads} style={S.board} onTouchEnd={onBoardTouchEndLeads}>
        {STATUS_COLS_LEAD.map(col => {
          const items = byStatus(col.key);
          const isTouchOver = touchOverColLeads === col.key;
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key}
              data-col-key={col.key}
              style={{
                ...S.col(col.color),
                // Destaque visual quando o dedo está sobre a coluna (touch drag)
                ...(isTouchOver ? { border: `2px solid ${col.color}`, background: `${col.color}11` } : {}),
              }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; dragOver.current = col.key; }}
              onDrop={e => onDrop(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => (
                <div key={l.id}
                  draggable
                  onDragStart={e => onDragStart(e, l)}
                  onTouchStart={e => onCardTouchStartLeads(e, l)}
                  style={S.card(dragging?.id === l.id || touchDragIdLeads === l.id)}>
                  <div style={S.cardName}>{l.cliente_nome}</div>
                  <div style={S.cardSub}>{l.cliente_cidade}</div>
                  <div style={S.cardFoot}>
                    {l.valor_total_venda
                      ? <span style={{ fontSize:11, color:'#f6c90e', fontWeight:700 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR')}</span>
                      : l.kit_valor
                        ? <span style={{ fontSize:11, color:'#94a3b8', fontWeight:600 }} title="Valor estimado do kit">≈ R$ {Number(l.kit_valor).toLocaleString('pt-BR')}</span>
                        : <span style={{ fontSize:11, color:'#475569' }}>—</span>
                    }
                    {!(isVendedor() && l.status === 'fechado') && (
                      <button style={S.editBtn} onClick={() => navigate('/clientes', { state: { leadId: l.id, from: '/controle' } })}>
                        <Pencil size={11} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {/* pointerEvents:none evita que o texto "Vazio" bloqueie o drop */}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0', pointerEvents:'none' }}>Vazio</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ─── Kanban do Pré Venda ──────────────────────────────────────────────────────
function KanbanPreVenda() {
  const navigate = useNavigate();
  const [leads, setLeads]       = useState([]);
  const [dragging, setDragging] = useState(null);
  const dragOver = useRef(null);
  const draggingPreRef = useRef(null); // ref para evitar stale closure

  // Modais
  const [followUpModal, setFollowUpModal] = useState(null); // { lead }
  const [vendedorModal, setVendedorModal] = useState(null); // { lead }
  const [fuData, setFuData]   = useState('');
  const [fuNota, setFuNota]   = useState('');
  const [prazo, setPrazo]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [erro, setErro]       = useState('');

  // Touch drag — iOS não suporta HTML5 DnD
  const handleDropLogicPre = (item, col) => {
    const atual = getPreVendaCol(item);
    if (atual === col) return;
    if (col === 'com_vendedor') { setVendedorModal({ lead: item }); return; }
    if (col === 'follow_up' && (atual === 'meus_leads' || atual === 'com_vendedor')) { setFollowUpModal({ lead: item }); return; }
    // 'meus_leads' — não permite voltar
  };
  const {
    boardRef: boardRefPre,
    touchDragId: touchDragIdPre,
    touchOverCol: touchOverColPre,
    onCardTouchStart: onCardTouchStartPre,
    onBoardTouchEnd: onBoardTouchEndPre,
    clearTouchOnDragStart: clearTouchPre,
  } = useTouchKanban(handleDropLogicPre);

  const [loadErro, setLoadErro] = useState('');
  const load = () => {
    setLoadErro('');
    api.get('/pre-venda/meus-leads/').then(r => setLeads(r.data)).catch(() => setLoadErro('Erro ao carregar dados. Tente atualizar a página.'));
  };

  useEffect(() => { load(); }, []);

  const byCol = (key) => leads.filter(l => getPreVendaCol(l) === key);

  const onDragStart = (e, lead) => {
    clearTouchPre(); // Android Chrome: desativa touch para o HTML5 DnD assumir
    draggingPreRef.current = lead;
    setDragging(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const onDrop = (e, col) => {
    e.preventDefault();
    const current = draggingPreRef.current || dragging;
    draggingPreRef.current = null;
    setDragging(null);
    if (!current) return;
    handleDropLogicPre(current, col);
  };

  const confirmarFollowUp = async () => {
    if (!fuData || !fuNota.trim()) { setErro('Preencha a data e a nota.'); return; }
    setSaving(true); setErro('');
    try {
      await api.post(`/leads/${followUpModal.lead.id}/followup-pre-venda/`, { data_contato: fuData, nota: fuNota.trim() });
      setFollowUpModal(null); setFuData(''); setFuNota('');
      load();
    } catch { setErro('Erro ao criar follow-up.'); }
    finally { setSaving(false); }
  };

  const confirmarVendedor = async () => {
    if (!prazo) { setErro('Defina o prazo para o vendedor entrar em contato.'); return; }
    setSaving(true); setErro('');
    try {
      await api.patch(`/pre-venda/meus-leads/${vendedorModal.lead.id}/`, { passar_para_vendedor: true, prazo_resposta_vendedor: prazo });
      setVendedorModal(null); setPrazo('');
      load();
    } catch { setErro('Erro ao transferir para vendedor.'); }
    finally { setSaving(false); }
  };

  const fecharModais = () => {
    setFollowUpModal(null); setVendedorModal(null);
    setFuData(''); setFuNota(''); setPrazo(''); setErro('');
  };

  const overlay = { position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' };
  const modal   = { background:'#111827', border:'1px solid rgba(246,201,14,0.25)', borderRadius:14, padding:28, width:420, maxWidth:'95vw' };

  return (
    <div>
      <div style={S.heading}>Controle — Pré Venda</div>
      <div style={S.sub}>Kanban de leads — arraste para adicionar follow-up ou passar para vendedor</div>

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      {/* Modal Follow Up */}
      {followUpModal && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:10 }}>Novo Follow Up</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{followUpModal.lead.cliente_nome}</strong>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={S.lbl}>Data do contato *</label>
              <input type="date" value={fuData} onChange={e => setFuData(e.target.value)} style={S.inp} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Nota *</label>
              <textarea value={fuNota} onChange={e => setFuNota(e.target.value)}
                style={{ ...S.inp, height:80, resize:'vertical' }}
                placeholder="O que foi combinado / próximo passo..." />
            </div>
            {erro && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{erro}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={fecharModais}>Cancelar</button>
              <button style={{ padding:'8px 20px', borderRadius:8, background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)', fontSize:13, fontWeight:700, cursor:'pointer', opacity: saving ? 0.6 : 1 }}
                onClick={confirmarFollowUp} disabled={saving}>{saving ? 'Salvando...' : 'Criar Follow Up'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Com Vendedor */}
      {vendedorModal && (
        <div style={overlay}>
          <div style={modal}>
            <div style={{ fontSize:16, fontWeight:700, color:'#4ade80', marginBottom:10 }}>Passar para Vendedor</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{vendedorModal.lead.cliente_nome}</strong>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Prazo para o vendedor entrar em contato *</label>
              <input type="datetime-local" value={prazo} onChange={e => setPrazo(e.target.value)} style={S.inp} />
            </div>
            {erro && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{erro}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={fecharModais}>Cancelar</button>
              <button style={{ padding:'8px 20px', borderRadius:8, background:'rgba(74,222,128,0.15)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.3)', fontSize:13, fontWeight:700, cursor:'pointer', opacity: saving ? 0.6 : 1 }}
                onClick={confirmarVendedor} disabled={saving}>{saving ? 'Enviando...' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}

      <div ref={boardRefPre} style={S.board} onTouchEnd={onBoardTouchEndPre}>
        {COLS_PRE_VENDA.map(col => {
          const items = byCol(col.key);
          const final = col.key === 'com_vendedor';
          const isTouchOver = touchOverColPre === col.key;
          return (
            <div key={col.key}
              data-col-key={col.key}
              style={{
                ...S.col(col.color),
                ...(isTouchOver ? { border: `2px solid ${col.color}`, background: `${col.color}11` } : {}),
              }}
              onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; dragOver.current = col.key; }}
              onDrop={e => onDrop(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={S.cardBody}>
              {items.map(l => (
                <div key={l.id}
                  draggable={!final}
                  onDragStart={e => !final && onDragStart(e, l)}
                  onTouchStart={e => !final && onCardTouchStartPre(e, l)}
                  style={S.card(dragging?.id === l.id || touchDragIdPre === l.id)}>
                  <div style={S.cardName}>{l.cliente_nome}</div>
                  <div style={S.cardSub}>{l.cliente_cidade || ''}</div>
                  {l.followups_count > 0 && (
                    <div style={{ fontSize:10, color:'#fb923c', marginTop:3 }}>
                      {l.followups_count} follow-up{l.followups_count > 1 ? 's' : ''}
                    </div>
                  )}
                  {l.vendedor_nome && (
                    <div style={{ fontSize:10, color:'#4ade80', marginTop:2 }}>👤 {l.vendedor_nome}</div>
                  )}
                  <div style={S.cardFoot}>
                    <span />
                    <button style={S.editBtn} onClick={() => navigate('/clientes', { state: { leadId: l.id, from: '/controle' } })}>
                      <Pencil size={11} />
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0', pointerEvents:'none' }}>Vazio</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Controle() {
  const { isPosVenda, isEngenheiro, isChefe, isGerenteComercial, isPreVenda } = useAuth();
  const [instaladoresListCtrl, setInstaladores] = useState([]);

  useEffect(() => {
    if (isPosVenda() || isChefe() || isGerenteComercial()) {
      api.get('/instaladores/').then(r => setInstaladores(r.data)).catch(() => {});
    }
  }, []);

  if (isPreVenda()) return <KanbanPreVenda />;

  if (isPosVenda()) return <KanbanPosVendaFull instaladoresList={instaladoresListCtrl} />;

  if (isEngenheiro()) return (
    <KanbanBoard
      title="Controle — Engenharia"
      sub="Kanban de projetos — arraste para qualquer status (sem restrição de fluxo)"
      cols={STATUS_COLS_ENG}
      endpoint="engenheiro"
      freeMode
    />
  );

  if (isChefe() || isGerenteComercial()) return <KanbanChefe />;
  return <KanbanLeads />;
}

// ─── Kanban do Chefe (tabs por processo) ──────────────────────────────────────
const TABS_CHEFE = [
  { key:'pre_venda',  label:'Pré Venda',  color:'#64748b' },
  { key:'vendedor',   label:'Vendedor',   color:'#60a5fa' },
  { key:'engenheiro', label:'Engenheiro', color:'#a78bfa' },
  { key:'pos_venda',  label:'Pós Venda',  color:'#fb923c' },
  { key:'instalador', label:'Instalador', color:'#4ade80' },
];

const COLS_PRE_VENDA = [
  { key:'meus_leads',   label:'Leads',         color:'#60a5fa' },
  { key:'follow_up',    label:'Follow Up',     color:'#fb923c' },
  { key:'com_vendedor', label:'Com Vendedor',  color:'#4ade80' },
];

const getPreVendaCol = (l) => {
  if (l.status === 'aguardando_vendedor') return 'com_vendedor';
  if ((l.followups_count || 0) > 0)       return 'follow_up';
  return 'meus_leads';
};

const COLS_ENG = [
  { key:'aguardando_engenheiro',         label:'Aguardando',               color:'#64748b' },
  { key:'a_pagar_crt',                   label:'A Pagar CRT',              color:'#fb923c' },
  { key:'analise_cemig',                 label:'Análise CEMIG',            color:'#60a5fa' },
  { key:'parecer_aprovado',              label:'Parecer Aprovado',         color:'#4ade80' },
  { key:'parecer_reprovado',             label:'Parecer Reprovado',        color:'#f87171' },
  { key:'aguardando_solicitar_vistoria', label:'Ag. Vistoria',             color:'#a78bfa' },
  { key:'vistoria_solicitada',           label:'Vistoria Solicitada',      color:'#f6c90e' },
  { key:'vistoria_concluida',            label:'Vistoria Concluída',       color:'#4ade80' },
  { key:'realizando_planejamento',       label:'Planejamento (leg.)',      color:'#334155' },
  { key:'aguardando_cemig',              label:'Ag. CEMIG (leg.)',         color:'#334155' },
  { key:'documentacao_aprovada',         label:'Doc. Aprovada (leg.)',     color:'#334155' },
  { key:'vistoria_feita',                label:'Vistoria Feita (leg.)',    color:'#334155' },
];

const COLS_POS = [
  { key:'aguardando',        label:'Aguardando',        color:'#64748b' },
  { key:'na_transportadora', label:'Na Transportadora',  color:'#fb923c' },
  { key:'processo_entrega',  label:'Processo de Entrega', color:'#facc15' },
  { key:'entregue',          label:'Entregue',           color:'#22d3ee' },
  { key:'instalando',        label:'Instalando',         color:'#a78bfa' },
  { key:'monitoramento',     label:'Monitoramento',      color:'#8b5cf6' },
  { key:'finalizado',        label:'Finalizado',         color:'#4ade80' },
];

const COLS_INST = [
  { key:'aguardando_instalador',  label:'Aguardando',          color:'#64748b' },
  { key:'instalacao_programada',  label:'Instal. Programada',  color:'#fb923c' },
  { key:'instalando',             label:'Instalando',           color:'#a78bfa' },
  { key:'instalacao_concluida',   label:'Concluída',            color:'#4ade80' },
];

// ─── Pipeline circles para busca de cliente (KanbanChefe) ─────────────────────
const _V_PIPE = new Set(['fechado','finalizado','vistoria_feita','instalacao_concluida','pago','concluido']);
const _R_PIPE = new Set(['aguardando_vendedor','aguardando_engenheiro','aguardando_instalador',
                          'aguardando_pagamento','aguardando_cemig','aguardando','perdido','cancelado']);
function stCorPipe(s) {
  if (!s) return '#1e2d45';
  if (_V_PIPE.has(s)) return '#4ade80';
  if (_R_PIPE.has(s)) return '#f87171';
  return '#f6c90e';
}
const PIPE_NOME = { V:'Vendedor', E:'Engenheiro', P:'Pós Venda', I:'Instalador', F:'Financeiro' };
const PIPE_LABEL = {
  aguardando_vendedor:'Aguardando', visita:'Visita', proposta:'Proposta', fechado:'Fechado',
  perdido:'Perdido', em_espera:'Aguardando', aguardando_engenheiro:'Aguardando',
  realizando_planejamento:'Planejamento', aguardando_cemig:'Ag. CEMIG',
  documentacao_aprovada:'Doc. Aprovada', vistoria_feita:'Vistoria OK',
  a_pagar_crt:'A Pagar CRT', analise_cemig:'Análise CEMIG', parecer_aprovado:'Parecer Aprovado',
  parecer_reprovado:'Parecer Reprovado', aguardando_solicitar_vistoria:'Ag. Vistoria',
  vistoria_solicitada:'Vistoria Solicitada', vistoria_concluida:'Vistoria Concluída',
  aguardando:'Aguardando', na_transportadora:'Transportadora', processo_entrega:'Em Entrega',
  entregue:'Entregue', instalando:'Instalando', finalizado:'Finalizado', monitoramento:'Monitoramento',
  aguardando_instalador:'Aguardando', instalacao_programada:'Programada', instalacao_concluida:'Concluída',
  aguardando_pagamento:'Ag. Pagamento', pago:'Pago',
};
// isPrevenda = lead ainda não entrou no pipeline (V E P I F ficam cinzas)
function PipelineCirclesCtrl({ lead, onTabClick }) {
  const isPrevenda = !lead.engenheiro_status && !lead.pos_venda_status && !lead.instalador_status
    && !['visita','proposta','em_espera','fechado','perdido'].includes(lead.status);
  const circles = [
    { letter:'V', status: isPrevenda ? null : lead.status,              tab:'vendedor'   },
    { letter:'E', status: isPrevenda ? null : lead.engenheiro_status,   tab:'engenheiro' },
    { letter:'P', status: isPrevenda ? null : lead.pos_venda_status,    tab:'pos_venda'  },
    { letter:'I', status: isPrevenda ? null : lead.instalador_status,   tab:'instalador' },
    { letter:'F', status: isPrevenda ? null : lead.financeiro_status,   tab: null        },
  ];
  return (
    <div style={{ display:'flex', gap:6, alignItems:'center', marginTop:6 }}>
      {circles.map(c => {
        const cor = stCorPipe(c.status);
        const lbl = c.status ? (PIPE_LABEL[c.status] || c.status) : 'Sem dados';
        const canClick = !isPrevenda && c.tab !== null;
        return (
          <div
            key={c.letter}
            title={`${PIPE_NOME[c.letter]}: ${lbl}`}
            onClick={canClick ? () => onTabClick(c.tab) : undefined}
            style={{
              width:28, height:28, borderRadius:'50%',
              background:`${cor}22`, border:`2px solid ${cor}`,
              color:cor, display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:11, fontWeight:700,
              cursor: canClick ? 'pointer' : 'default',
              userSelect:'none', transition:'transform 0.12s',
            }}
            onMouseEnter={canClick ? e => { e.currentTarget.style.transform='scale(1.18)'; } : undefined}
            onMouseLeave={canClick ? e => { e.currentTarget.style.transform='scale(1)'; } : undefined}
          >
            {c.letter}
          </div>
        );
      })}
    </div>
  );
}

// ── Helpers de data para filtro do Kanban ────────────────────────────────────
const _kIsoToDDMM = (iso) => { if (!iso) return ''; const [y,m,d]=iso.split('-'); return `${d}/${m}/${y}`; };
const _kDdmmToISO = (ddmm) => {
  if (!ddmm || ddmm.length < 10) return '';
  const parts = ddmm.split('/');
  if (parts.length < 3 || parts[2].length < 4) return '';
  const [d, m, y] = parts;
  if (parseInt(m,10) < 1 || parseInt(m,10) > 12 || parseInt(d,10) < 1 || parseInt(d,10) > 31) return '';
  return `${y}-${m.padStart(2,'0')}-${d.padStart(2,'0')}`;
};
const _kMaskDate = (val) => {
  const digits = val.replace(/\D/g,'').slice(0,8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0,2)}/${digits.slice(2)}`;
  return `${digits.slice(0,2)}/${digits.slice(2,4)}/${digits.slice(4)}`;
};
// Converte um Date ou string ISO (UTC) para YYYY-MM-DD no fuso de Brasília.
// criado_em vem da API em UTC; .slice(0,10) daria a data UTC, que diverge da
// data BRT para leads criados entre 00h-03h UTC (21h-00h BRT do dia anterior).
const _brDateISO = (d) => {
  try {
    return new Date(d).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' });
  } catch {
    return typeof d === 'string' ? d.slice(0, 10) : '';
  }
};
// Input de data compacto — o input[type=date] é sobreposto ao ícone,
// funcionando nativamente em iOS/Android/Safari sem showPicker().
function DatePickerInputK({ value, onChange }) {
  return (
    <div style={{ display:'flex', alignItems:'center' }}>
      <input
        type="text"
        placeholder="dd/mm/aaaa"
        maxLength={10}
        value={value}
        onChange={e => onChange(_kMaskDate(e.target.value))}
        style={{ padding:'7px 0 7px 8px', background:'transparent', border:'none', color: value ? '#e2e8f0' : '#475569', fontSize:12, outline:'none', width:90 }}
      />
      <div style={{ position:'relative', display:'flex', alignItems:'center', padding:'0 8px 0 2px' }}>
        <Calendar size={13} color="#475569" style={{ pointerEvents:'none' }} />
        <input
          type="date"
          tabIndex={-1}
          style={{ position:'absolute', inset:0, opacity:0, fontSize:16, cursor:'pointer', width:'100%', height:'100%' }}
          onChange={e => e.target.value && onChange(_kIsoToDDMM(e.target.value))}
        />
      </div>
    </div>
  );
}

function KanbanPosVendaFull({ instaladoresList }) {
  const [tab, setTab] = useState('pos_venda');

  const TABS = [
    { key:'pos_venda',  label:'Pós Venda',  color:'#fb923c' },
    { key:'engenheiro', label:'Engenheiro', color:'#a78bfa' },
  ];

  return (
    <div>
      {/* ── Seletor de abas ── */}
      <div style={{ display:'flex', gap:8, marginBottom:0, flexWrap:'wrap', alignItems:'center',
        padding:'12px 0 0 4px' }}>
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'6px 14px', borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer',
              background: tab === t.key ? `${t.color}22` : 'transparent',
              color: tab === t.key ? t.color : '#475569',
              border:'none',
              borderBottom: tab === t.key ? `2px solid ${t.color}` : '2px solid transparent',
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pos_venda' && (
        <KanbanBoard
          title="Controle — Pós Venda"
          sub="Kanban de pós-venda — arraste para qualquer status (sem restrição de fluxo)"
          cols={STATUS_COLS_POS_VENDA}
          endpoint="pos-venda"
          freeMode
          instaladoresList={instaladoresList}
        />
      )}

      {tab === 'engenheiro' && (
        <KanbanBoard
          title="Controle — Engenharia"
          sub="Kanban de projetos — arraste para qualquer status (sem restrição de fluxo)"
          cols={STATUS_COLS_ENG}
          endpoint="engenheiro"
          freeMode
        />
      )}
    </div>
  );
}

function KanbanChefe() {
  const navigate = useNavigate();
  // Lê filtros salvos na sessão (persiste enquanto a aba do browser estiver aberta)
  const _ss = (() => { try { return JSON.parse(sessionStorage.getItem('crm_controle_filtros') || '{}'); } catch { return {}; } })();
  const [tab, setTab]           = useState(_ss.tab || 'vendedor');
  const [meus, setMeus]         = useState(_ss.meus || false);
  const [leads, setLeads]       = useState([]);
  const [dragging, setDragging] = useState(null);
  const [moveModal, setMoveModal]   = useState(null);
  const [vendaModal, setVendaModal] = useState(null);
  const [propostaModal, setPropostaModal] = useState(null);
  // Engenheiro / Pós Venda / Instalador — drag livre (sem travas), igual ao kanban dos funcionários
  const [engMoveModal, setEngMoveModal]   = useState(null);  // { item, newStatus }
  const [posMoveModal, setPosMoveModal]   = useState(null);  // { item, newStatus }
  const [instMoveModal, setInstMoveModal] = useState(null);  // { item, newStatus }
  // Modais customizados para o instalador
  const [instInstalando, setInstInstalando]     = useState(null);  // { item }
  const [instProgramada, setInstProgramada]     = useState(null);  // { item }
  const [instConcluida, setInstConcluida]       = useState(null);  // { item, dataConclusao }
  const [instDataConclusao, setInstDataConclusao] = useState('');
  // Pré Venda — mesmos modais/fluxo do KanbanPreVenda (follow-up / passar p/ vendedor)
  const [preFollowUpModal, setPreFollowUpModal] = useState(null); // { lead }
  const [preVendedorModal, setPreVendedorModal] = useState(null); // { lead }
  const [preFuData, setPreFuData] = useState('');
  const [preFuNota, setPreFuNota] = useState('');
  const [prePrazo, setPrePrazo]   = useState('');
  const [preSaving, setPreSaving] = useState(false);
  const [preErro, setPreErro]     = useState('');
  // Com Vendedor → Leads/Follow Up: chefe devolve para Pré Venda (mesmo fluxo do Clientes.js)
  const [voltarPreVendaModal, setVoltarPreVendaModal] = useState(null); // { lead }
  const [vpvNota, setVpvNota]                 = useState('');
  const [vpvResponsavelId, setVpvResponsavelId] = useState('');
  const [vpvSaving, setVpvSaving]              = useState(false);
  const [vpvErro, setVpvErro]                  = useState('');
  const [preVendaFuncionarios, setPreVendaFuncionarios] = useState([]);
  const [posInstaladores, setPosInstaladores] = useState([]);
  const dragOver = useRef(null);
  const draggingRef = useRef(null); // backup para evitar stale closure no onDrop
  const [loadErro, setLoadErro] = useState('');
  // ── Busca de cliente ─────────────────────────────────────────────────────────
  const [searchQuery, setSearchQuery]   = useState('');
  const [showSugg, setShowSugg]         = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const searchRef                        = useRef(null);
  // ── Filtro de funcionário ────────────────────────────────────────────────────
  const [empQuery, setEmpQuery]         = useState('');
  const [showEmpSugg, setShowEmpSugg]   = useState(false);
  const [selectedEmp, setSelectedEmp]   = useState(null);
  const empRef                           = useRef(null);
  // ── Filtro de data (criação do lead) ─────────────────────────────────────────
  const [filtroDataIni, setFiltroDataIni] = useState(_ss.filtroDataIni || '');
  const [filtroDataFim, setFiltroDataFim] = useState(_ss.filtroDataFim || '');
  const [inputDataIni, setInputDataIni]   = useState(_ss.inputDataIni  || '');
  const [inputDataFim, setInputDataFim]   = useState(_ss.inputDataFim  || '');

  // ── Touch drag (iOS/Android) ─────────────────────────────────────────────────
  // Ref atualizado a cada render com o dispatch correto para a aba ativa.
  const touchDropRef = useRef(null);
  const {
    boardRef:           boardRefChefe,
    touchDragId:        touchDragIdChefe,
    touchOverCol:       touchOverColChefe,
    onCardTouchStart:   onCardTouchStartChefe,
    onBoardTouchEnd:    onBoardTouchEndChefe,
    clearTouchOnDragStart: clearTouchChefe,
  } = useTouchKanban((item, colKey) => touchDropRef.current?.(item, colKey));

  const load = () => {
    const params = meus ? '?meus=true' : '';
    setLoadErro('');
    api.get('/leads/' + params).then(r => setLeads(r.data)).catch(() => setLoadErro('Erro ao carregar dados. Tente atualizar a página.'));
  };

  useEffect(() => { load(); }, [meus]);

  // Persiste filtros no sessionStorage para sobreviver a navegações dentro da sessão
  useEffect(() => {
    try {
      sessionStorage.setItem('crm_controle_filtros', JSON.stringify({
        tab, meus, filtroDataIni, filtroDataFim, inputDataIni, inputDataFim,
      }));
    } catch {}
  }, [tab, meus, filtroDataIni, filtroDataFim, inputDataIni, inputDataFim]); // eslint-disable-line

  // Lista de responsáveis de Pré Venda (para o modal de devolver lead) — mesma fonte do Clientes.js
  useEffect(() => {
    api.get('/usuarios/')
      .then(r => setPreVendaFuncionarios(r.data.filter(u => u.perfil_acesso === 4 && u.ativo !== false)))
      .catch(() => {});
  }, []);

  // Lista de instaladores (pessoas) — para o modal de arrastar para Entregue no pós-venda
  useEffect(() => {
    api.get('/instaladores/').then(r => setPosInstaladores(r.data)).catch(() => {});
  }, []);

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const h = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSugg(false);
      if (empRef.current   && !empRef.current.contains(e.target))     setShowEmpSugg(false);
    };
    document.addEventListener('mousedown', h);
    document.addEventListener('touchstart', h, { passive: true });
    return () => {
      document.removeEventListener('mousedown', h);
      document.removeEventListener('touchstart', h);
    };
  }, []); // eslint-disable-line

  // Reset filtro de funcionário ao trocar de aba (sempre limpa ao trocar)
  useEffect(() => { setSelectedEmp(null); setEmpQuery(''); setShowEmpSugg(false); }, [tab]); // eslint-disable-line

  // Atualizar selectedLead com dados frescos quando leads recarrega
  useEffect(() => {
    if (!selectedLead) return;
    const updated = leads.find(l => l.id === selectedLead.id);
    if (updated) setSelectedLead(updated);
    else setSelectedLead(null);
  }, [leads]); // eslint-disable-line

  // ── Helpers ─────────────────────────────────────────────────────────────────
  // Campo de nome de funcionário por aba (Engenheiro e Pós Venda sem filtro individual)
  const EMP_FIELD = {
    vendedor:  'vendedor_nome',
    instalador:'instalador_nome',
    pre_venda: 'responsavel_pre_venda_nome',
  };
  const empField = EMP_FIELD[tab] || null;

  // Leads filtrados por data de criação
  const leadsData = (filtroDataIni || filtroDataFim)
    ? leads.filter(l => {
        // Converte criado_em (UTC) para data BRT antes de comparar
        const d = l.criado_em ? _brDateISO(l.criado_em) : '';
        if (filtroDataIni && d < filtroDataIni) return false;
        if (filtroDataFim && d > filtroDataFim) return false;
        return true;
      })
    : leads;

  // Datas para atalhos e detecção de filtro ativo — calculadas em BRT
  const _kIsoHoje   = _brDateISO(new Date());
  const _kIsoSemAtr = _brDateISO(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
  const _kIsoMesAtr = (() => {
    const d = new Date(); d.setMonth(d.getMonth() - 1);
    return _brDateISO(d);
  })();
  const ativoSemK = filtroDataIni === _kIsoSemAtr && filtroDataFim === _kIsoHoje;
  const ativoMesK = filtroDataIni === _kIsoMesAtr && filtroDataFim === _kIsoHoje;
  const btnDataStyle = (ativo) => ({
    background: ativo ? 'rgba(246,201,14,0.12)' : 'transparent',
    color: ativo ? '#f6c90e' : '#64748b',
    border: `1px solid ${ativo ? 'rgba(246,201,14,0.35)' : '#1a2035'}`,
    borderRadius:8, padding:'7px 12px', fontSize:12, fontWeight:600,
    cursor:'pointer', whiteSpace:'nowrap',
  });

  const filterEmp = (arr) => {
    if (!selectedEmp || !empField) return arr;
    return arr.filter(l => (l[empField] || '') === selectedEmp);
  };
  const filterLead = (arr) => selectedLead ? arr.filter(l => l.id === selectedLead.id) : arr;
  const applyFilters = (arr) => filterEmp(filterLead(arr));

  const byLeadStatus = (key) => applyFilters(leadsData.filter(l => l.status === key));
  const byEngStatus  = (key) => applyFilters(leadsData.filter(l => l.status === 'fechado' && l.engenheiro_status === key));
  const byPosStatus  = (key) => applyFilters(leadsData.filter(l => l.status === 'fechado' && l.pos_venda_status === key));
  const byInstStatus = (key) => applyFilters(leadsData.filter(l => l.status === 'fechado' && l.instalador_status === key));

  // Lista de funcionários únicos para a aba atual (derivada dos leads filtrados por data)
  const empList = empField
    ? [...new Set(leadsData.map(l => l[empField]).filter(Boolean))].sort()
    : [];
  const empSuggestions = empQuery.length >= 1
    ? empList.filter(n => n.toLowerCase().includes(empQuery.toLowerCase()))
    : empList;

  const suggestions = searchQuery.length >= 3
    ? leads.filter(l => (l.cliente_nome || '').toLowerCase().includes(searchQuery.toLowerCase())).slice(0, 8)
    : [];

  const colsMap = Object.fromEntries(STATUS_COLS_LEAD.map(c => [c.key, c.label]));
  const colsMapEng  = Object.fromEntries(COLS_ENG.map(c => [c.key, c.label]));
  const colsMapPos  = Object.fromEntries(COLS_POS.map(c => [c.key, c.label]));
  const colsMapInst = Object.fromEntries(COLS_INST.map(c => [c.key, c.label]));

  const salvarConversaChefe = async (leadId, texto) => {
    if (!texto || !texto.trim()) return;
    try { await api.post(`/leads/${leadId}/conversas/`, { texto: texto.trim() }); } catch (e) {
      alert('Erro ao salvar conversa: ' + (e.response?.data?.error || e.message || 'tente novamente'));
    }
  };

  // ── Drag & Drop (todas as abas) ─────────────────────────────────────────────
  const onDragStart = (e, lead) => {
    clearTouchChefe(); // Android Chrome: evita conflito entre HTML5 DnD e touch events
    draggingRef.current = lead; setDragging(lead); e.dataTransfer.effectAllowed = 'move';
  };
  const confirm = async ({ visitaDate, motivo, prazo, dataPrevista, dataChegada, dataConclusao, observacoes, conversaTexto } = {}) => {
    if (!moveModal) return;
    try {
      const ns = moveModal.newStatus;
      const body = { status: ns };
      if (ns === 'visita'    && visitaDate)      body.data_visita = visitaDate;
      if (ns === 'em_espera') {
        if (motivo) body.motivo_espera = motivo;
        if (prazo)  body.prazo_resposta_vendedor = prazo;
      }
      if (ns === 'perdido'   && motivo)          body.motivo_desistencia = motivo;
      await api.patch(`/leads/${moveModal.lead.id}/`, body);
      await salvarConversaChefe(moveModal.lead.id, conversaTexto);
    } catch (err) {
      alert('Erro ao salvar: ' + (err?.response?.data?.error || err?.response?.data?.erro || err?.message || 'tente novamente'));
    }
    setMoveModal(null); load();
  };
  const handleSelectStatus = (s) => {
    if (s === 'fechado' && moveModal) { setVendaModal({ lead: moveModal.lead, erro: '' }); setMoveModal(null); }
    else setMoveModal(m => ({ ...m, newStatus: s }));
  };
  const confirmVenda = async ({ nome, rg, cpf_cnpj, kit_id, valor_total_venda, conversaTexto }) => {
    if (!vendaModal) return;
    try {
      const body = { status: 'fechado', cliente_nome: nome, rg, cpf_cnpj, kit: kit_id };
      if (valor_total_venda != null) body.valor_total_venda = valor_total_venda;
      await api.patch(`/leads/${vendaModal.lead.id}/`, body);
      await salvarConversaChefe(vendaModal.lead.id, conversaTexto);
      setVendaModal(null); load();
    } catch (err) {
      setVendaModal(v => ({ ...v, erro: err?.response?.data?.erro || 'Erro ao confirmar venda.' }));
    }
  };

  // ── Engenheiro — drag livre, igual KanbanBoard freeMode ────────────────────
  const onDropEng = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    if (!current || current.engenheiro_status === newStatus) return;
    setEngMoveModal({ item: current, newStatus });
  };
  const confirmEng = async () => {
    if (!engMoveModal) return;
    try { await api.patch(`/engenheiro/${engMoveModal.item.engenheiro_id}/`, { status: engMoveModal.newStatus }); } catch {}
    setEngMoveModal(null); load();
  };

  // ── Pós Venda — drag livre, igual KanbanBoard freeMode ──────────────────────
  const onDropPos = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    if (!current || current.pos_venda_status === newStatus) return;
    if (newStatus === 'processo_entrega') {
      api.patch(`/pos-venda/${current.pos_venda_id}/`, { status: newStatus }).then(load).catch(() => {});
      return;
    }
    setPosMoveModal({ item: current, newStatus });
  };
  const confirmPos = async (extra = {}) => {
    if (!posMoveModal) return;
    const body = { status: posMoveModal.newStatus };
    if (posMoveModal.newStatus === 'entregue' && extra.dataEntrega) body.data_entrega = extra.dataEntrega;
    try { await api.patch(`/pos-venda/${posMoveModal.item.pos_venda_id}/`, body); } catch {}
    // Designar instalador + data prevista ao mover para Entregue (o backend avança
    // o status do instalador para "instalacao_programada" automaticamente)
    if (posMoveModal.newStatus === 'entregue' && extra.instaladorId && extra.dataPrevista) {
      const instJobId = posMoveModal.item.instalador_id; // instalador_id = job record ID no LeadSerializer
      if (instJobId) {
        await api.patch(`/instalador/${instJobId}/`, { instalador: extra.instaladorId, data_prevista: extra.dataPrevista }).catch(() => {});
      }
    }
    setPosMoveModal(null); load();
  };

  // ── Instalador — drag livre ──────────────────────────────────────────────────
  const onDropInst = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    if (!current || current.instalador_status === newStatus) return;
    if (newStatus === 'instalando') {
      setInstInstalando({ item: current });
    } else if (newStatus === 'instalacao_programada') {
      setInstProgramada({ item: current });
    } else if (newStatus === 'instalacao_concluida') {
      // Pré-preencher com horário atual de Brasília
      const now = new Date();
      const brt = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
      const pad = n => String(n).padStart(2, '0');
      const dt = `${brt.getFullYear()}-${pad(brt.getMonth()+1)}-${pad(brt.getDate())}T${pad(brt.getHours())}:${pad(brt.getMinutes())}`;
      setInstDataConclusao(dt);
      setInstConcluida({ item: current });
    } else {
      setInstMoveModal({ item: current, newStatus });
    }
  };
  const confirmInst = async () => {
    if (!instMoveModal) return;
    try { await api.patch(`/instalador/${instMoveModal.item.instalador_id}/`, { status: instMoveModal.newStatus }); } catch {}
    setInstMoveModal(null); load();
  };
  const confirmInstProgramada = async () => {
    if (!instProgramada) return;
    try { await api.patch(`/instalador/${instProgramada.item.instalador_id}/`, { status: 'instalacao_programada' }); } catch {}
    setInstProgramada(null); load();
  };
  const confirmInstInstalando = async () => {
    if (!instInstalando) return;
    try { await api.patch(`/instalador/${instInstalando.item.instalador_id}/`, { status: 'instalando' }); } catch {}
    setInstInstalando(null); load();
  };
  const confirmInstConcluida = async () => {
    if (!instConcluida) return;
    try {
      await api.patch(`/instalador/${instConcluida.item.instalador_id}/`, {
        status: 'instalacao_concluida',
        data_conclusao: instDataConclusao || null,
      });
    } catch {}
    setInstConcluida(null); setInstDataConclusao(''); load();
  };

  // ── Pré Venda — mesmo fluxo do KanbanPreVenda (follow-up / passar p/ vendedor),
  // mais a autonomia do chefe de devolver de "Com Vendedor" para Leads/Follow Up.
  const onDropPre = (e, col) => {
    e.preventDefault();
    const atualLead = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    if (!atualLead) return;
    const atual = getPreVendaCol(atualLead);
    if (atual === col) return;
    // Com Vendedor → Leads ou Follow Up: devolver para Pré Venda (igual ao "Enviar para Pré Venda" do Clientes.js)
    if (atual === 'com_vendedor' && (col === 'meus_leads' || col === 'follow_up')) {
      setVoltarPreVendaModal({ lead: atualLead });
      return;
    }
    if (col === 'com_vendedor' && atual === 'meus_leads') {
      setPreVendedorModal({ lead: atualLead });
    } else if (col === 'follow_up' && atual === 'meus_leads') {
      setPreFollowUpModal({ lead: atualLead });
    }
  };
  const confirmVoltarPreVenda = async () => {
    if (!vpvNota.trim()) { setVpvErro('Informe o motivo.'); return; }
    setVpvSaving(true); setVpvErro('');
    try {
      await api.post(`/leads/${voltarPreVendaModal.lead.id}/enviar-pre-venda/`, {
        nota: vpvNota.trim(),
        responsavel_id: vpvResponsavelId || null,
      });
      setVoltarPreVendaModal(null); setVpvNota(''); setVpvResponsavelId('');
      load();
    } catch (err) {
      setVpvErro(err?.response?.data?.error || 'Erro ao devolver para Pré Venda.');
    } finally { setVpvSaving(false); }
  };
  const fecharVoltarPreVenda = () => {
    setVoltarPreVendaModal(null); setVpvNota(''); setVpvResponsavelId(''); setVpvErro('');
  };
  const confirmarPreFollowUp = async () => {
    if (!preFuData || !preFuNota.trim()) { setPreErro('Preencha a data e a nota.'); return; }
    setPreSaving(true); setPreErro('');
    try {
      await api.post(`/leads/${preFollowUpModal.lead.id}/followup-pre-venda/`, { data_contato: preFuData, nota: preFuNota.trim() });
      setPreFollowUpModal(null); setPreFuData(''); setPreFuNota('');
      load();
    } catch { setPreErro('Erro ao criar follow-up.'); }
    finally { setPreSaving(false); }
  };
  const confirmarPreVendedor = async () => {
    if (!prePrazo) { setPreErro('Defina o prazo para o vendedor entrar em contato.'); return; }
    setPreSaving(true); setPreErro('');
    try {
      await api.patch(`/pre-venda/meus-leads/${preVendedorModal.lead.id}/`, { passar_para_vendedor: true, prazo_resposta_vendedor: prePrazo });
      setPreVendedorModal(null); setPrePrazo('');
      load();
    } catch { setPreErro('Erro ao transferir para vendedor.'); }
    finally { setPreSaving(false); }
  };
  const fecharPreModais = () => {
    setPreFollowUpModal(null); setPreVendedorModal(null);
    setPreFuData(''); setPreFuNota(''); setPrePrazo(''); setPreErro('');
  };

  // ── Card genérico ────────────────────────────────────────────────────────────
  const renderCard = (l, draggable = false, statusField = null) => {
    const isDragging = dragging?.id === l.id || touchDragIdChefe === l.id;
    return (
      <div key={l.id}
        draggable={draggable}
        onDragStart={draggable ? (e => onDragStart(e, l)) : undefined}
        onTouchStart={draggable ? (e => onCardTouchStartChefe(e, l)) : undefined}
        style={S.card(isDragging)}>
        <div style={S.cardName}>{l.cliente_nome}</div>
        <div style={S.cardSub}>{l.cliente_cidade || ''}</div>
        {statusField && (
          <div style={{ fontSize:10, color:'#64748b', marginTop:3 }}>{l[statusField] || '—'}</div>
        )}
        <div style={S.cardFoot}>
          {l.valor_total_venda
            ? <span style={{ fontSize:11, color:'#f6c90e', fontWeight:600 }}>R$ {Number(l.valor_total_venda).toLocaleString('pt-BR')}</span>
            : <span />}
          <button style={S.editBtn} onClick={() => navigate('/clientes', { state: { leadId: l.id, from: '/controle' } })}>
            <Pencil size={11} />
          </button>
        </div>
      </div>
    );
  };

  // ── onDrop (KanbanChefe) ─────────────────────────────────────────────────
  // Sem travas: qualquer status → qualquer status. Único caso especial é "fechado",
  // que mantém o fluxo de checagem de documentos (PropostaModal / VendaFechadaModal).
  const onDropChefe = (e, newStatus) => {
    e.preventDefault();
    const current = draggingRef.current || dragging;
    draggingRef.current = null;
    setDragging(null);
    if (!current || current.status === newStatus) return;
    if (newStatus === 'fechado') {
      if (current.status === 'proposta' || current.status === 'em_espera') {
        setPropostaModal({ lead: current });
      } else {
        setVendaModal({ lead: current, erro: '' });
      }
      return;
    }
    setMoveModal({ lead: current, newStatus });
  };

  return (
    <div>
      <div style={S.heading}>Controle — Visão Geral</div>

      {/* ── Filtro de data de criação ─────────────────────────────────────── */}
      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:10 }}>
        <button onClick={() => {
          setInputDataIni(_kIsoToDDMM(_kIsoSemAtr)); setInputDataFim(_kIsoToDDMM(_kIsoHoje));
          setFiltroDataIni(_kIsoSemAtr); setFiltroDataFim(_kIsoHoje);
        }} style={btnDataStyle(ativoSemK)}>Análise semanal</button>
        <button onClick={() => {
          setInputDataIni(_kIsoToDDMM(_kIsoMesAtr)); setInputDataFim(_kIsoToDDMM(_kIsoHoje));
          setFiltroDataIni(_kIsoMesAtr); setFiltroDataFim(_kIsoHoje);
        }} style={btnDataStyle(ativoMesK)}>Análise mensal</button>
        <div style={{ display:'flex', alignItems:'center', background:'#0f1117', border:'1px solid #1a2035', borderRadius:8, overflow:'hidden' }}>
          <DatePickerInputK value={inputDataIni} onChange={setInputDataIni} />
          <span style={{ color:'#475569', fontSize:12, padding:'0 2px', userSelect:'none' }}>até</span>
          <DatePickerInputK value={inputDataFim} onChange={setInputDataFim} />
        </div>
        <button
          onClick={() => { setFiltroDataIni(_kDdmmToISO(inputDataIni)); setFiltroDataFim(_kDdmmToISO(inputDataFim)); }}
          style={{ padding:'7px 14px', borderRadius:8, background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)', color:'#60a5fa', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap' }}
        >Buscar</button>
        {(filtroDataIni || filtroDataFim) && (
          <button onClick={() => { setFiltroDataIni(''); setFiltroDataFim(''); setInputDataIni(''); setInputDataFim(''); }}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', fontSize:12, padding:0 }}>
            ✕ Limpar datas
          </button>
        )}
      </div>

      {/* ── Busca de cliente + filtro de funcionário ─────────────────────── */}
      <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap' }}>
      <div ref={searchRef} style={{ position:'relative', flex:1, minWidth:200 }}>
        <div style={{ display:'flex', alignItems:'center', background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, padding:'6px 12px', gap:8 }}>
          <Search size={15} color="#64748b" />
          <input
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              setShowSugg(true);
              if (!e.target.value) setSelectedLead(null);
            }}
            onFocus={() => { if (searchQuery.length >= 3) setShowSugg(true); }}
            placeholder="Buscar cliente por nome (mín. 3 letras)…"
            style={{ flex:1, background:'transparent', border:'none', outline:'none', color:'#e2e8f0', fontSize:13 }}
          />
          {(selectedLead || searchQuery) && (
            <button
              onClick={() => { setSearchQuery(''); setSelectedLead(null); setShowSugg(false); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', padding:0, lineHeight:1, display:'flex' }}
            ><X size={15} /></button>
          )}
        </div>
        {showSugg && suggestions.length > 0 && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, zIndex:200, marginTop:4, maxHeight:260, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
            {suggestions.map(l => (
              <div
                key={l.id}
                onClick={() => { setSelectedLead(l); setSearchQuery(l.cliente_nome || ''); setShowSugg(false); }}
                style={{ padding:'9px 14px', cursor:'pointer', color:'#e2e8f0', fontSize:13, borderBottom:'1px solid #0f172a' }}
                onMouseEnter={e => e.currentTarget.style.background = '#253048'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                {l.cliente_nome}
                {l.cliente_cidade ? <span style={{ color:'#64748b', fontSize:12, marginLeft:6 }}>{l.cliente_cidade}</span> : null}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Filtro de funcionário (só para abas com atribuição por pessoa) ── */}
      {empField && <div ref={empRef} style={{ position:'relative', flex:1, minWidth:200 }}>
        <div style={{ display:'flex', alignItems:'center', background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, padding:'6px 12px', gap:8 }}>
          <Search size={15} color="#64748b" />
          <input
            value={empQuery}
            onChange={e => { setEmpQuery(e.target.value); setShowEmpSugg(true); if (!e.target.value) setSelectedEmp(null); }}
            onFocus={() => setShowEmpSugg(true)}
            placeholder={`Filtrar por ${TABS_CHEFE.find(t => t.key === tab)?.label ?? 'funcionário'}…`}
            style={{ flex:1, background:'transparent', border:'none', outline:'none', color:'#e2e8f0', fontSize:13 }}
          />
          {(selectedEmp || empQuery) && (
            <button
              onClick={() => { setEmpQuery(''); setSelectedEmp(null); setShowEmpSugg(false); }}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', padding:0, lineHeight:1, display:'flex' }}
            ><X size={15} /></button>
          )}
          {/* Seta para abrir lista completa */}
          <button
            onClick={() => setShowEmpSugg(s => !s)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#64748b', padding:0, lineHeight:1, display:'flex' }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
        {showEmpSugg && empSuggestions.length > 0 && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, zIndex:200, marginTop:4, maxHeight:260, overflowY:'auto', boxShadow:'0 8px 24px rgba(0,0,0,0.4)' }}>
            {empSuggestions.map(nome => (
              <div
                key={nome}
                onClick={() => { setSelectedEmp(nome); setEmpQuery(nome); setShowEmpSugg(false); }}
                style={{ padding:'9px 14px', cursor:'pointer', color:'#e2e8f0', fontSize:13, borderBottom:'1px solid #0f172a',
                  background: selectedEmp === nome ? '#253048' : 'transparent' }}
                onMouseEnter={e => e.currentTarget.style.background = '#253048'}
                onMouseLeave={e => e.currentTarget.style.background = selectedEmp === nome ? '#253048' : 'transparent'}
              >
                {nome}
              </div>
            ))}
          </div>
        )}
        {showEmpSugg && empSuggestions.length === 0 && (
          <div style={{ position:'absolute', top:'100%', left:0, right:0, background:'#1a2035', border:'1px solid #2a3550', borderRadius:8, zIndex:200, marginTop:4, padding:'9px 14px', color:'#64748b', fontSize:13 }}>
            Nenhum funcionário encontrado
          </div>
        )}
      </div>}

      </div>{/* fim do flex busca+funcionário */}

      {selectedLead && (
        <div style={{ marginBottom:14, padding:'10px 14px', background:'#1a2035', border:'1px solid #2a3550', borderRadius:8 }}>
          <h3 style={{ margin:0, fontSize:16, fontWeight:700, color:'#e2e8f0' }}>
            <span
              onClick={() => navigate(`/jornada/${selectedLead.cliente_id}`)}
              style={{ cursor:'pointer', textDecoration:'underline', textDecorationColor:'#64748b', color:'#e2e8f0' }}
            >
              {selectedLead.cliente_nome}
            </span>
          </h3>
          <PipelineCirclesCtrl lead={selectedLead} onTabClick={t => setTab(t)} />
        </div>
      )}

      {loadErro && (
        <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13, marginBottom:14 }}>{loadErro}</div>
      )}

      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        {TABS_CHEFE.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding:'6px 14px', borderRadius:7, fontSize:12, fontWeight:700, cursor:'pointer',
              background: tab === t.key ? `${t.color}22` : 'transparent',
              color: tab === t.key ? t.color : '#475569',
              border:'none',
              borderBottom: tab === t.key ? `2px solid ${t.color}` : '2px solid transparent',
            }}>
            {t.label}
          </button>
        ))}
        <div style={{ display:'flex', background:'#1a2035', borderRadius:8, overflow:'hidden', border:'1px solid #2a3550', marginLeft:'auto' }}>
          <button
            onClick={() => setMeus(false)}
            style={{ padding:'6px 14px', fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
              background: !meus ? '#3b82f6' : 'transparent',
              color: !meus ? '#fff' : '#94a3b8' }}
          >Todos</button>
          <button
            onClick={() => setMeus(true)}
            style={{ padding:'6px 14px', fontSize:12, fontWeight:600, border:'none', cursor:'pointer',
              background: meus ? '#3b82f6' : 'transparent',
              color: meus ? '#fff' : '#94a3b8' }}
          >Apenas meus</button>
        </div>
      </div>

      {vendaModal && (
        <VendaFechadaModal
          lead={vendaModal.lead}
          erro={vendaModal.erro}
          onConfirm={confirmVenda}
          onCancel={() => setVendaModal(null)}
        />
      )}

      {propostaModal && (
        <PropostaModal
          lead={propostaModal.lead}
          onConfirm={(apresentou) => {
            const lead = propostaModal.lead;
            setPropostaModal(null);
            if (apresentou) setVendaModal({ lead, erro: '' });
          }}
          onCancel={() => setPropostaModal(null)}
        />
      )}

      {moveModal && (
        <KanbanModal
          itemName={moveModal.lead.cliente_nome}
          currentStatus={moveModal.lead.status}
          newStatus={moveModal.newStatus}
          colsMap={colsMap}
          flowMap={null}
          hint={MODAL_HINTS[moveModal.newStatus]}
          onConfirm={confirm}
          onCancel={() => setMoveModal(null)}
          onSelectStatus={handleSelectStatus}
        />
      )}

      {engMoveModal && (
        <SimpleConfirmModal
          itemName={engMoveModal.item.cliente_nome}
          currentStatus={engMoveModal.item.engenheiro_status}
          newStatus={engMoveModal.newStatus}
          colsMap={colsMapEng}
          onConfirm={confirmEng}
          onCancel={() => setEngMoveModal(null)}
        />
      )}

      {posMoveModal && (
        posMoveModal.newStatus === 'entregue' ? (
          <EntregaDataModal
            itemName={posMoveModal.item.cliente_nome}
            currentStatus={posMoveModal.item.pos_venda_status}
            newStatus={posMoveModal.newStatus}
            colsMap={colsMapPos}
            onConfirm={(extra) => confirmPos(extra)}
            onCancel={() => setPosMoveModal(null)}
            instaladoresList={posInstaladores}
            defaultDataPrevista={posMoveModal.item.instalador_data_prevista || ''}
            kitData={posMoveModal.item}
          />
        ) : (
          <SimpleConfirmModal
            itemName={posMoveModal.item.cliente_nome}
            currentStatus={posMoveModal.item.pos_venda_status}
            newStatus={posMoveModal.newStatus}
            colsMap={colsMapPos}
            onConfirm={() => confirmPos()}
            onCancel={() => setPosMoveModal(null)}
            kitData={posMoveModal.item}
          />
        )
      )}

      {instMoveModal && (
        <SimpleConfirmModal
          itemName={instMoveModal.item.cliente_nome}
          currentStatus={instMoveModal.item.instalador_status}
          newStatus={instMoveModal.newStatus}
          colsMap={colsMapInst}
          onConfirm={confirmInst}
          onCancel={() => setInstMoveModal(null)}
        />
      )}

      {/* ── Modal: Instalação Programada ─────────────────────────────────────── */}
      {instProgramada && (() => {
        const item = instProgramada.item;
        const qty = item.quantidade_placas || 0;
        const total = (qty * 90).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#0d1526', border:'1px solid #1a2035', borderRadius:14, padding:'28px 28px 24px', width:'min(460px,100%)', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:6 }}>Instalação Programada</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
                Cliente: <strong style={{ color:'#e2e8f0' }}>{item.cliente_nome}</strong>
              </div>
              {/* Ganhos */}
              <div style={{ background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:14, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                {qty > 0 ? (
                  <>💰 Você ganhará <strong>R$ {total}</strong> nesta instalação!
                  <div style={{ fontSize:12, fontWeight:400, color:'#86efac', marginTop:4 }}>{qty} placas × R$90,00 por placa</div></>
                ) : (
                  <>💰 Você ganhará <strong>R$ 90,00 por placa</strong> nesta instalação!</>
                )}
              </div>
              {/* Kit */}
              {(item.kit_equipamento || item.kit_marca) && (
                <div style={{ background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:12 }}>
                  <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📦 Kit Solar</div>
                  {[
                    ['Equipamento', item.kit_equipamento],
                    ['Marca',       item.kit_marca],
                    ['Geração',     item.kit_geracao != null ? `${item.kit_geracao} kWh/mês` : null],
                    ['Placas',      item.quantidade_placas != null ? `${item.quantidade_placas} un · ${item.kit_placas_w || '—'} W cada` : null],
                    ['Inversores',  item.kit_qtd_inversores != null ? `${item.kit_qtd_inversores} un` : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                      <span style={{ color:'#64748b' }}>{k}</span>
                      <span style={{ color:'#e2e8f0', fontWeight:600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setInstProgramada(null)} style={{ padding:'9px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                <button onClick={confirmInstProgramada} style={{ padding:'9px 20px', borderRadius:8, background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)', fontSize:13, fontWeight:700, cursor:'pointer' }}>Confirmar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal: Instalando ─────────────────────────────────────────────────── */}
      {instInstalando && (() => {
        const item = instInstalando.item;
        const qty = item.quantidade_placas || 0;
        const total = (qty * 90).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#0d1526', border:'1px solid #1a2035', borderRadius:14, padding:'28px 28px 24px', width:'min(460px,100%)', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:6 }}>Iniciar Instalação</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
                Cliente: <strong style={{ color:'#e2e8f0' }}>{item.cliente_nome}</strong>
              </div>
              {/* Ganhos */}
              <div style={{ background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:14, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                {qty > 0 ? (
                  <>💰 Você ganhará <strong>R$ {total}</strong> nesta instalação!
                  <div style={{ fontSize:12, fontWeight:400, color:'#86efac', marginTop:4 }}>{qty} placas × R$90,00 por placa</div></>
                ) : (
                  <>💰 Você ganhará <strong>R$ 90,00 por placa</strong> nesta instalação!</>
                )}
              </div>
              {/* Kit */}
              {(item.kit_equipamento || item.kit_marca) && (
                <div style={{ background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:20, fontSize:12 }}>
                  <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📦 Kit Solar</div>
                  {[
                    ['Equipamento', item.kit_equipamento],
                    ['Marca',       item.kit_marca],
                    ['Geração',     item.kit_geracao != null ? `${item.kit_geracao} kWh/mês` : null],
                    ['Placas',      item.quantidade_placas != null ? `${item.quantidade_placas} un · ${item.kit_placas_w || '—'} W cada` : null],
                    ['Inversores',  item.kit_qtd_inversores != null ? `${item.kit_qtd_inversores} un` : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                      <span style={{ color:'#64748b' }}>{k}</span>
                      <span style={{ color:'#e2e8f0', fontWeight:600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button onClick={() => setInstInstalando(null)} style={{ padding:'9px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                <button onClick={confirmInstInstalando} style={{ padding:'9px 20px', borderRadius:8, background:'rgba(167,139,250,0.15)', color:'#a78bfa', border:'1px solid rgba(167,139,250,0.3)', fontSize:13, fontWeight:700, cursor:'pointer' }}>Confirmar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Modal: Instalação Concluída ───────────────────────────────────────── */}
      {instConcluida && (() => {
        const item = instConcluida.item;
        const qty = item.quantidade_placas || 0;
        const total = (qty * 90).toLocaleString('pt-BR', { minimumFractionDigits: 2 });
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
            <div style={{ background:'#0d1526', border:'1px solid #1a2035', borderRadius:14, padding:'28px 28px 24px', width:'min(520px,100%)', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.6)' }}>
              <div style={{ fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:6 }}>Concluir Instalação</div>
              <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
                Cliente: <strong style={{ color:'#e2e8f0' }}>{item.cliente_nome}</strong>
              </div>

              <div style={{ background:'rgba(34,197,94,0.08)', border:'1px solid rgba(34,197,94,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:14, fontSize:13, color:'#4ade80', fontWeight:600 }}>
                {qty > 0 ? (
                  <>💰 Você ganhará <strong>R$ {total}</strong> nesta instalação!
                  <div style={{ fontSize:12, fontWeight:400, color:'#86efac', marginTop:4 }}>{qty} placas × R$90,00 por placa</div></>
                ) : (
                  <>💰 Você ganhará <strong>R$ 90,00 por placa</strong> nesta instalação!</>
                )}
              </div>

              {(item.kit_equipamento || item.kit_marca) && (
                <div style={{ background:'rgba(96,165,250,0.07)', border:'1px solid rgba(96,165,250,0.2)', borderRadius:10, padding:'12px 16px', marginBottom:14, fontSize:12 }}>
                  <div style={{ fontSize:11, color:'#60a5fa', fontWeight:700, textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>📦 Kit Solar</div>
                  {[
                    ['Equipamento', item.kit_equipamento],
                    ['Marca',       item.kit_marca],
                    ['Geração',     item.kit_geracao != null ? `${item.kit_geracao} kWh/mês` : null],
                    ['Placas',      item.quantidade_placas != null ? `${item.quantidade_placas} un · ${item.kit_placas_w || '—'} W cada` : null],
                    ['Inversores',  item.kit_qtd_inversores != null ? `${item.kit_qtd_inversores} un` : null],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
                      <span style={{ color:'#64748b' }}>{k}</span>
                      <span style={{ color:'#e2e8f0', fontWeight:600 }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginBottom:14 }}>
                <label style={{ fontSize:12, color:'#94a3b8', fontWeight:600, display:'block', marginBottom:6 }}>Data e hora da conclusão (Brasília)</label>
                <input
                  type="datetime-local"
                  value={instDataConclusao}
                  onChange={e => setInstDataConclusao(e.target.value)}
                  style={{ width:'100%', padding:'9px 12px', borderRadius:8, background:'#0a0f1e', border:'1px solid #1a2035', color:'#e2e8f0', fontSize:13 }}
                />
              </div>

              <FotosInstalacaoPanel leadId={item.lead} readonly={false} initialOpen={true} />

              <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
                <button onClick={() => { setInstConcluida(null); setInstDataConclusao(''); }} style={{ padding:'9px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }}>Cancelar</button>
                <button onClick={confirmInstConcluida} style={{ padding:'9px 20px', borderRadius:8, background:'rgba(34,197,94,0.15)', color:'#4ade80', border:'1px solid rgba(34,197,94,0.3)', fontSize:13, fontWeight:700, cursor:'pointer' }}>✓ Concluir Instalação</button>
              </div>
            </div>
          </div>
        );
      })()}

      {preFollowUpModal && (
        <div style={S.preOverlay}>
          <div style={S.preModal}>
            <div style={{ fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:10 }}>Novo Follow Up</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{preFollowUpModal.lead.cliente_nome}</strong>
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={S.lbl}>Data do contato *</label>
              <input type="date" value={preFuData} onChange={e => setPreFuData(e.target.value)} style={S.inp} />
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Nota *</label>
              <textarea value={preFuNota} onChange={e => setPreFuNota(e.target.value)}
                style={{ ...S.inp, height:80, resize:'vertical' }}
                placeholder="O que foi combinado / próximo passo..." />
            </div>
            {preErro && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{preErro}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={fecharPreModais}>Cancelar</button>
              <button style={{ padding:'8px 20px', borderRadius:8, background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)', fontSize:13, fontWeight:700, cursor:'pointer', opacity: preSaving ? 0.6 : 1 }}
                onClick={confirmarPreFollowUp} disabled={preSaving}>{preSaving ? 'Salvando...' : 'Criar Follow Up'}</button>
            </div>
          </div>
        </div>
      )}

      {preVendedorModal && (
        <div style={S.preOverlay}>
          <div style={S.preModal}>
            <div style={{ fontSize:16, fontWeight:700, color:'#4ade80', marginBottom:10 }}>Passar para Vendedor</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{preVendedorModal.lead.cliente_nome}</strong>
            </div>
            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Prazo para o vendedor entrar em contato *</label>
              <input type="datetime-local" value={prePrazo} onChange={e => setPrePrazo(e.target.value)} style={S.inp} />
            </div>
            {preErro && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{preErro}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={fecharPreModais}>Cancelar</button>
              <button style={{ padding:'8px 20px', borderRadius:8, background:'rgba(74,222,128,0.15)', color:'#4ade80', border:'1px solid rgba(74,222,128,0.3)', fontSize:13, fontWeight:700, cursor:'pointer', opacity: preSaving ? 0.6 : 1 }}
                onClick={confirmarPreVendedor} disabled={preSaving}>{preSaving ? 'Enviando...' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}

      {voltarPreVendaModal && (
        <div style={S.preOverlay}>
          <div style={S.preModal}>
            <div style={{ fontSize:16, fontWeight:700, color:'#fb923c', marginBottom:10 }}>→ Enviar para Pré Venda</div>
            <div style={{ fontSize:13, color:'#94a3b8', marginBottom:16 }}>
              Cliente: <strong style={{ color:'#e2e8f0' }}>{voltarPreVendaModal.lead.cliente_nome}</strong>
            </div>
            {preVendaFuncionarios.length > 0 && (
              <div style={{ marginBottom:10 }}>
                <label style={S.lbl}>Responsável Pré Venda (opcional)</label>
                <select value={vpvResponsavelId} onChange={e => setVpvResponsavelId(e.target.value)} style={S.inp}>
                  <option value="">— Sem responsável específico —</option>
                  {preVendaFuncionarios.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </select>
              </div>
            )}
            <div style={{ marginBottom:16 }}>
              <label style={S.lbl}>Follow Up / Motivo *</label>
              <textarea value={vpvNota} onChange={e => setVpvNota(e.target.value)}
                style={{ ...S.inp, height:80, resize:'vertical' }}
                placeholder="Descreva o motivo de retornar ao Pré Venda..." />
            </div>
            {vpvErro && <div style={{ color:'#f87171', fontSize:12, marginBottom:10 }}>{vpvErro}</div>}
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
              <button style={{ padding:'8px 18px', borderRadius:8, background:'transparent', color:'#94a3b8', border:'1px solid #1e2d45', fontSize:13, cursor:'pointer' }} onClick={fecharVoltarPreVenda}>Cancelar</button>
              <button style={{ padding:'8px 20px', borderRadius:8, background:'rgba(251,146,60,0.15)', color:'#fb923c', border:'1px solid rgba(251,146,60,0.3)', fontSize:13, fontWeight:700, cursor:'pointer', opacity: vpvSaving ? 0.6 : 1 }}
                onClick={confirmVoltarPreVenda} disabled={vpvSaving}>{vpvSaving ? 'Enviando...' : 'Confirmar'}</button>
            </div>
          </div>
        </div>
      )}

      {/* touchDropRef atualizado a cada render para capturar tab/state atuais */}
      {(() => {
        touchDropRef.current = (item, colKey) => {
          if (tab === 'vendedor') {
            if (!item || item.status === colKey) return;
            if (colKey === 'fechado') {
              if (item.status === 'proposta' || item.status === 'em_espera') setPropostaModal({ lead: item });
              else setVendaModal({ lead: item, erro: '' });
              return;
            }
            setMoveModal({ lead: item, newStatus: colKey });
          } else if (tab === 'engenheiro') {
            if (!item || item.engenheiro_status === colKey) return;
            setEngMoveModal({ item, newStatus: colKey });
          } else if (tab === 'pos_venda') {
            if (!item || item.pos_venda_status === colKey) return;
            if (colKey === 'processo_entrega') {
              api.patch(`/pos-venda/${item.pos_venda_id}/`, { status: colKey }).then(load).catch(() => {});
              return;
            }
            setPosMoveModal({ item, newStatus: colKey });
          } else if (tab === 'instalador') {
            if (!item || item.instalador_status === colKey) return;
            if (colKey === 'instalando') { setInstInstalando({ item }); }
            else if (colKey === 'instalacao_programada') { setInstProgramada({ item }); }
            else if (colKey === 'instalacao_concluida') {
              const now = new Date();
              const brt = new Date(now.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
              const pad = n => String(n).padStart(2, '0');
              const dt = `${brt.getFullYear()}-${pad(brt.getMonth()+1)}-${pad(brt.getDate())}T${pad(brt.getHours())}:${pad(brt.getMinutes())}`;
              setInstDataConclusao(dt); setInstConcluida({ item });
            } else { setInstMoveModal({ item, newStatus: colKey }); }
          } else if (tab === 'pre_venda') {
            if (!item) return;
            const atual = getPreVendaCol(item);
            if (atual === colKey) return;
            if (atual === 'com_vendedor' && (colKey === 'meus_leads' || colKey === 'follow_up')) {
              setVoltarPreVendaModal({ lead: item }); return;
            }
            if (colKey === 'com_vendedor' && atual === 'meus_leads') setPreVendedorModal({ lead: item });
            else if (colKey === 'follow_up' && atual === 'meus_leads') setPreFollowUpModal({ lead: item });
          }
        };
        return null;
      })()}

      <div ref={boardRefChefe} style={S.board} onTouchEnd={onBoardTouchEndChefe}>
        {tab === 'vendedor' && STATUS_COLS_LEAD.map(col => {
          const items = byLeadStatus(col.key);
          const isDraggable = true; // sem travas: qualquer card, inclusive fechado/perdido, é arrastável
          const isTouchOver = touchOverColChefe === col.key;
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key} data-col-key={col.key}
              style={{ ...S.col(col.color), ...(isTouchOver ? { border:`2px solid ${col.color}`, background:`${col.color}11` } : {}) }}
              onDragOver={e => { e.preventDefault(); dragOver.current = col.key; }}
              onDrop={e => onDropChefe(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => renderCard(l, isDraggable))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0' }}>Vazio</div>}
              </div>
            </div>
          );
        })}

        {tab === 'pos_venda' && COLS_POS.map(col => {
          const items = byPosStatus(col.key);
          const isTouchOver = touchOverColChefe === col.key;
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key} data-col-key={col.key}
              style={{ ...S.col(col.color), ...(isTouchOver ? { border:`2px solid ${col.color}`, background:`${col.color}11` } : {}) }}
              onDragOver={e => { e.preventDefault(); dragOver.current = col.key; }}
              onDrop={e => onDropPos(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => renderCard(l, true, 'pos_venda_status'))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0' }}>Vazio</div>}
              </div>
            </div>
          );
        })}

        {tab === 'engenheiro' && COLS_ENG.map(col => {
          const items = byEngStatus(col.key);
          const isTouchOver = touchOverColChefe === col.key;
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key} data-col-key={col.key}
              style={{ ...S.col(col.color), ...(isTouchOver ? { border:`2px solid ${col.color}`, background:`${col.color}11` } : {}) }}
              onDragOver={e => { e.preventDefault(); dragOver.current = col.key; }}
              onDrop={e => onDropEng(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => renderCard(l, true, 'engenheiro_status'))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0' }}>Vazio</div>}
              </div>
            </div>
          );
        })}

        {tab === 'instalador' && COLS_INST.map(col => {
          const items = byInstStatus(col.key);
          const isTouchOver = touchOverColChefe === col.key;
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key} data-col-key={col.key}
              style={{ ...S.col(col.color), ...(isTouchOver ? { border:`2px solid ${col.color}`, background:`${col.color}11` } : {}) }}
              onDragOver={e => { e.preventDefault(); dragOver.current = col.key; }}
              onDrop={e => onDropInst(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => renderCard(l, true, 'instalador_status'))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0' }}>Vazio</div>}
              </div>
            </div>
          );
        })}

        {tab === 'pre_venda' && COLS_PRE_VENDA.map(col => {
          const items = applyFilters(leadsData.filter(l => getPreVendaCol(l) === col.key));
          const isTouchOver = touchOverColChefe === col.key;
          // chefe tem autonomia total: inclusive os cards de "Com Vendedor" são arrastáveis (pode devolver p/ Pré Venda)
          const soma = items.reduce((s,l) => s + (Number(l.valor_total_venda)||0), 0);
          return (
            <div key={col.key} data-col-key={col.key}
              style={{ ...S.col(col.color), ...(isTouchOver ? { border:`2px solid ${col.color}`, background:`${col.color}11` } : {}) }}
              onDragOver={e => { e.preventDefault(); dragOver.current = col.key; }}
              onDrop={e => onDropPre(e, col.key)}>
              <div style={S.colHead}>
                <span style={S.colLabel(col.color)}>{col.label}</span>
                <span style={S.badge(col.color)}>{items.length}</span>
              </div>
              <div style={{ fontSize:11, fontWeight: soma > 0 ? 600 : 400, color: soma > 0 ? '#f6c90e' : '#3d4f6b', marginBottom:8 }}>
                {soma > 0 ? `Total: R$ ${soma.toLocaleString('pt-BR')}` : 'Sem nenhum valor'}
              </div>
              <div style={S.cardBody}>
              {items.map(l => renderCard(l, true))}
              {items.length === 0 && <div style={{ color:'#1e2536', fontSize:12, textAlign:'center', padding:'12px 0' }}>Vazio</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}